import React, { useState, useEffect, useCallback } from "react";
import { db, auth, storage } from "./firebase";
import {
  collection, addDoc, getDocs, doc, updateDoc, query,
  orderBy, onSnapshot, where, getDoc, serverTimestamp,
  query as firestoreQuery
} from "firebase/firestore";
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// ─── Mailer Config ────────────────────────────────────────────────
// Upload mailer.php to your Hostinger File Manager (public_html root)
// Then this URL will work automatically — no changes needed.
const MAILER_URL = "https://novacert.in/mailer.php";

// Helper: send email via your Hostinger PHP mailer
const sendEmail = async (subject, bodyLines) => {
  const res = await fetch(MAILER_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subject, message: bodyLines.join("\n") }),
  });
  const data = await res.json();
  if (!data.success) throw new Error(data.message);
};
// ─────────────────────────────────────────────────────────────────

// ─── Utility Functions ───
const generateCertNumber = (plan) => {
  const prefix = plan === "credseal" ? "NC-CS" : "NC-PC";
  const year = new Date().getFullYear();
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}-${year}-${rand}`;
};

const generateAppId = () => {
  const year = new Date().getFullYear();
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `NCA-${year}-${rand}`;
};

const formatDate = (d) => {
  if (!d) return "—";
  const date = d.toDate ? d.toDate() : new Date(d);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const getExpiryDate = (issueDate) => {
  const d = new Date(issueDate);
  d.setFullYear(d.getFullYear() + 1);
  return d;
};

const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  const d = expiryDate.toDate ? expiryDate.toDate() : new Date(expiryDate);
  return d < new Date();
};

const STATUS_COLORS = {
  pending:      { bg: "#FEF3C7", text: "#92400E", label: "Pending Review" },
  under_review: { bg: "#DBEAFE", text: "#1E40AF", label: "Under Review" },
  approved:     { bg: "#D1FAE5", text: "#065F46", label: "Certified" },
  rejected:     { bg: "#FEE2E2", text: "#991B1B", label: "Rejected" },
  expired:      { bg: "#F3F4F6", text: "#6B7280", label: "Expired" },
};

const PLANS = {
  professional: {
    name: "Professional Certification",
    price: 4999, gst: 899.82, total: 5898.82, renewal: 3300,
    badge: "NovaCert Verified", color: "#0D9488",
    features: [
      "Portfolio & client satisfaction review",
      "Certification badge for websites & proposals",
      "40–45 day audit process",
      "1 year validity",
    ],
  },
  credseal: {
    name: "Advanced Certification + CredSeal™",
    price: 9999, gst: 1799.82, total: 11798.82, renewal: 5300,
    badge: "NovaCert CredSeal™ Verified", color: "#7C3AED",
    features: [
      "Everything in Professional",
      "CredSeal™ credibility standard",
      "Backed by governing body",
      "Featured in NovaCert Directory",
      "Social media spotlight",
    ],
  },
};

// ─── Icon Components ───
const Icons = {
  Shield: () => ( <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> ),
  Check: () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg> ),
  Search: () => ( <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> ),
  ArrowRight: () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg> ),
  Star: () => ( <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> ),
  Clock: () => ( <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> ),
  Upload: () => ( <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg> ),
  X: () => ( <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg> ),
  Menu: () => ( <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg> ),
  Eye: () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg> ),
  AlertTriangle: () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> ),
  Logout: () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg> ),
  Loader: () => ( <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{animation:"spin 1s linear infinite"}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg> ),
  FileText: () => ( <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> ),
};

// ─── Global Styles ───
const fontLink = "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=Playfair+Display:wght@400;600;700;800&display=swap";
const globalStyles = `
@import url('${fontLink}');
*{margin:0;padding:0;box-sizing:border-box}
html{overflow-x:hidden}
body{font-family:'DM Sans',sans-serif;background:#FAFAF9;color:#1C1917;overflow-x:hidden;width:100%;max-width:100vw}
.nova-gradient{background:linear-gradient(135deg,#0F172A 0%,#1E293B 50%,#0F172A 100%)}
.nova-btn{display:inline-flex;align-items:center;gap:8px;padding:14px 28px;border-radius:8px;font-weight:600;font-size:15px;border:none;cursor:pointer;transition:all .2s;font-family:'DM Sans',sans-serif}
.nova-btn:disabled{opacity:.5;cursor:not-allowed}
.nova-btn-primary{background:#0D9488;color:#fff}.nova-btn-primary:hover:not(:disabled){background:#0F766E;transform:translateY(-1px)}
.nova-btn-secondary{background:transparent;color:#0D9488;border:2px solid #0D9488}.nova-btn-secondary:hover{background:#0D9488;color:#fff}
.nova-btn-dark{background:#1E293B;color:#fff}.nova-btn-dark:hover{background:#334155}
.nova-btn-danger{background:#DC2626;color:#fff}.nova-btn-danger:hover{background:#B91C1C}
.nova-btn-sm{padding:8px 16px;font-size:13px}
.nova-card{background:#fff;border-radius:16px;border:1px solid #D6D3D1;transition:all .3s;overflow:hidden}
.nova-card:hover{box-shadow:0 20px 60px rgba(0,0,0,.08)}
.nova-input{width:100%;padding:12px 16px;border:2px solid #D6D3D1;border-radius:10px;font-size:15px;font-family:'DM Sans',sans-serif;transition:border-color .2s;background:#fff;color:#1C1917;outline:none}
.nova-input:focus{border-color:#0D9488;background:#fff;box-shadow:0 0 0 3px rgba(13,148,136,.1)}
.nova-input::placeholder{color:#9CA3AF}
.nova-label{display:block;font-weight:600;font-size:13px;color:#374151;margin-bottom:6px;text-transform:uppercase;letter-spacing:.5px}
.status-badge{display:inline-flex;align-items:center;gap:6px;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600}
.status-dot{width:6px;height:6px;border-radius:50%}
.fade-in{animation:fadeIn .5s ease-out}
@keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
.slide-up{animation:slideUp .6s ease-out}
@keyframes slideUp{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
@keyframes spin{to{transform:rotate(360deg)}}
.pattern-bg{background-image:radial-gradient(circle at 1px 1px,rgba(255,255,255,.08) 1px,transparent 0);background-size:40px 40px}
textarea.nova-input{resize:vertical;min-height:80px}
.nova-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:24px}
.nova-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:20px}
.nova-flex-wrap{display:flex;flex-wrap:wrap;gap:12px}
img{max-width:100%;height:auto}
section{max-width:100%;overflow-x:hidden}
@media(max-width:768px){
  .hide-mobile{display:none !important}
  .nova-btn{padding:12px 20px;font-size:14px}
  .nova-grid-2{grid-template-columns:1fr !important}
  .nova-grid-3{grid-template-columns:1fr !important}
  .nova-mobile-stack{flex-direction:column !important}
  .nova-mobile-full{width:100% !important;min-width:unset !important;max-width:100% !important}
  .nova-mobile-pad{padding-left:16px !important;padding-right:16px !important}
  .nova-mobile-center{text-align:center !important}
  .nova-mobile-hide{display:none !important}
}
@media(max-width:480px){
  .nova-btn{padding:10px 16px;font-size:13px}
  .nova-input{font-size:14px}
}
`;


// ═══════════════════════════════════════════
// ─── MAIN APP COMPONENT ──────────────────
// ═══════════════════════════════════════════
export default function App() {
  const [page, setPage] = useState("home");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [viewingApp, setViewingApp] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [lastAppId, setLastAppId] = useState(null);
  const [lastSubmission, setLastSubmission] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [proVerifyResult, setProVerifyResult] = useState(null);

  const navigate = (p, plan) => {
    setPage(p);
    if (plan) setSelectedPlan(plan);
    setMobileMenu(false);
    setViewingApp(null);
    setVerifyResult(null);
    window.scrollTo?.(0, 0);
  };

  const openPost = (post) => { setSelectedPost(post); navigate("blog-post"); };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setAdminUser(user);
      setAuthChecked(true);
    });
    return unsub;
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setAdminUser(null);
    navigate("home");
  };

  return (
    <>
      <style>{globalStyles}</style>

      {/* ── NAVIGATION ── */}
      <nav style={{position:"sticky",top:0,zIndex:100,background:"rgba(250,250,249,0.92)",backdropFilter:"blur(20px)",borderBottom:"1px solid #E7E5E4"}}>
        <div style={{maxWidth:1200,margin:"0 auto",padding:"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:70}}>
          <div onClick={()=>navigate("home")} style={{cursor:"pointer",display:"flex",alignItems:"center",gap:10}}>
            <div style={{width:38,height:38,borderRadius:10,background:"linear-gradient(135deg,#0D9488,#0F766E)",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}><Icons.Shield/></div>
            <div>
              <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:22,color:"#0F172A"}}>NovaCert</span>
              <span style={{fontSize:9,display:"block",color:"#4B5563",letterSpacing:2,textTransform:"uppercase",marginTop:-2}}>Excellence Verified</span>
            </div>
          </div>
          <div className="hide-mobile" style={{display:"flex",alignItems:"center",gap:8}}>
            {[["home","Home"],["certifications","Certifications"],["professional","For Professionals"],["directory","Directory"],["blog","Blog"],["verify","Verify"],["pro-verify","Verify Professional"],["empanel","Join as Auditor"],["contact","Contact"],["admin","Admin"]].map(([key,label])=>(
              <button key={key} onClick={()=>navigate(key)} style={{padding:"8px 16px",background:page===key?"#F0FDFA":"transparent",color:page===key?"#0D9488":"#374151",border:"none",borderRadius:8,fontSize:14,fontWeight:500,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>{label}</button>
            ))}
            {adminUser && <button onClick={handleLogout} style={{padding:"8px 12px",background:"none",border:"none",cursor:"pointer",color:"#4B5563",display:"flex",alignItems:"center",gap:4,fontFamily:"'DM Sans'"}}><Icons.Logout/> Logout</button>}
          </div>
          <button onClick={()=>setMobileMenu(!mobileMenu)} style={{display:"none",background:"none",border:"none",cursor:"pointer",color:"#1C1917"}} className="show-mobile">
            {mobileMenu ? <Icons.X/> : <Icons.Menu/>}
          </button>
        </div>
        <style>{`@media(max-width:768px){.show-mobile{display:block!important}}`}</style>
        {mobileMenu && (
          <div style={{background:"#fff",borderTop:"1px solid #E7E5E4",padding:"16px 24px"}}>
            {[["home","Home"],["certifications","Certifications"],["professional","For Professionals"],["directory","Certified Directory"],["blog","Blog"],["verify","Verify Certificate"],["pro-verify","Verify Professional"],["empanel","Join as Auditor/Consultant"],["contact","Contact Us"],["admin","Admin Panel"]].map(([key,label])=>(
              <button key={key} onClick={()=>navigate(key)} style={{display:"block",width:"100%",textAlign:"left",padding:"12px 0",background:"none",border:"none",borderBottom:"1px solid #F5F5F4",fontSize:15,fontWeight:500,color:page===key?"#0D9488":"#374151",cursor:"pointer",fontFamily:"'DM Sans'"}}>{label}</button>
            ))}
            {adminUser && <button onClick={handleLogout} style={{display:"block",width:"100%",textAlign:"left",padding:"12px 0",background:"none",border:"none",fontSize:15,color:"#DC2626",cursor:"pointer",fontFamily:"'DM Sans'"}}>Logout</button>}
          </div>
        )}
      </nav>

      {/* ── PAGES ── */}
      <div className="fade-in" key={page}>
        {page === "home"           && <HomePage onNavigate={navigate}/>}
        {page === "certifications" && <CertificationsPage onSelectPlan={(plan)=>navigate("apply",plan)}/>}
        {page === "apply"          && selectedPlan && <ApplicationForm plan={selectedPlan} onSubmit={(appId, planKey, formData)=>{ setLastAppId(appId); setLastSubmission({planKey, formData}); navigate("success"); }} onBack={()=>navigate("certifications")}/>}
        {page === "success"        && <SuccessPage appId={lastAppId} submission={lastSubmission} onNavigate={navigate}/>}
        {page === "verify"         && <VerifyPage result={verifyResult} setResult={setVerifyResult}/>}
        {page === "contact"        && <ContactPage/>}
        {page === "terms"          && <TermsPage onNavigate={navigate}/>}
        {page === "empanel"        && <EmpanelPage onNavigate={navigate}/>}
        {page === "empanel-success"&& <EmpanelSuccessPage onNavigate={navigate}/>}
        {page === "blog"           && <BlogPage onNavigate={navigate} onOpenPost={openPost}/>}
        {page === "blog-post"      && <BlogPostPage post={selectedPost} onNavigate={navigate}/>}
        {page === "directory"      && <DirectoryPage onNavigate={navigate}/>}
        {page === "professional"   && <ProfessionalPage onNavigate={navigate}/>}
        {page === "pro-apply"      && <ProfessionalForm onSubmit={()=>navigate("pro-success")} onBack={()=>navigate("professional")}/>}
        {page === "pro-success"    && <ProSuccessPage onNavigate={navigate}/>}
        {page === "pro-verify"     && <ProVerifyPage result={proVerifyResult} setResult={setProVerifyResult}/>}
        {page === "admin"          && !adminUser && <AdminLogin authChecked={authChecked}/>}
        {page === "admin"          && adminUser  && <AdminDashboard viewing={viewingApp} onView={setViewingApp}/>}
      </div>

      {/* ── FOOTER ── */}
      <footer style={{background:"#0F172A",color:"#94A3B8",padding:"48px 24px 32px",marginTop:80}}>
        <div style={{maxWidth:1200,margin:"0 auto",display:"flex",flexWrap:"wrap",gap:40,justifyContent:"space-between"}}>
          <div style={{flex:"1 1 280px"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
              <div style={{width:32,height:32,borderRadius:8,background:"#0D9488",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff"}}><Icons.Shield/></div>
              <span style={{fontFamily:"'Playfair Display',serif",fontWeight:700,fontSize:20,color:"#fff"}}>NovaCert</span>
            </div>
            <p style={{fontSize:13,lineHeight:1.7,maxWidth:320}}>Empowering freelancers, small businesses, and service providers to demonstrate their commitment to excellence.</p>
          </div>
          <div>
            <h4 style={{color:"#fff",fontSize:14,fontWeight:600,marginBottom:12}}>Quick Links</h4>
            {[["Home","home"],["Certifications","certifications"],["For Professionals","professional"],["Certified Directory","directory"],["Blog","blog"],["Verify Certificate","verify"],["Verify Professional","pro-verify"],["Join as Auditor","empanel"],["Contact Us","contact"]].map(([l,k])=>(
              <div key={l} onClick={()=>navigate(k)} style={{fontSize:13,padding:"4px 0",cursor:"pointer",transition:"color .2s"}} onMouseEnter={e=>e.target.style.color="#0D9488"} onMouseLeave={e=>e.target.style.color="#94A3B8"}>{l}</div>
            ))}
          </div>
          <div>
            <h4 style={{color:"#fff",fontSize:14,fontWeight:600,marginBottom:12}}>Contact Us</h4>
            <p style={{fontSize:13,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              <span>✉</span> <a href="mailto:info@novacert.in" style={{color:"#94A3B8",textDecoration:"none"}}>info@novacert.in</a>
            </p>
            <p style={{fontSize:13,marginBottom:8,display:"flex",alignItems:"center",gap:6}}>
              <span>📞</span> <a href="tel:+919115019666" style={{color:"#94A3B8",textDecoration:"none"}}>+91 91150 19666</a>
            </p>
            <p style={{fontSize:12,color:"#64748B",marginTop:12}}>Mon–Sat, 10am–6pm IST</p>
          </div>
        </div>
        <div style={{maxWidth:1200,margin:"32px auto 0",paddingTop:24,borderTop:"1px solid #1E293B",textAlign:"center",fontSize:12}}>
          © {new Date().getFullYear()} NovaCert. All rights reserved.
          <span style={{margin:"0 8px",color:"#334155"}}>|</span>
          <span onClick={()=>navigate("terms")} style={{cursor:"pointer",color:"#64748B",transition:"color .2s"}} onMouseEnter={e=>e.target.style.color="#0D9488"} onMouseLeave={e=>e.target.style.color="#64748B"}>Terms of Use</span>
        </div>
      </footer>
    </>
  );
}


// ═══════════════════════════════════════════
// ─── HOME PAGE ───────────────────────────
// ═══════════════════════════════════════════
function HomePage({ onNavigate }) {
  return (
    <>
      <section style={{padding:"clamp(56px,10vw,100px) 16px clamp(48px,8vw,80px)",textAlign:"center",color:"#fff",position:"relative",overflow:"hidden",background:"linear-gradient(135deg,#0F172A 0%,#1E293B 50%,#0F172A 100%)",backgroundImage:"linear-gradient(135deg,#0F172A 0%,#1E293B 50%,#0F172A 100%)"}}>
        <div style={{position:"absolute",top:-100,right:-100,width:400,height:400,borderRadius:"50%",background:"rgba(13,148,136,0.1)",filter:"blur(80px)"}}/>
        <div style={{position:"relative",maxWidth:800,margin:"0 auto"}} className="slide-up">
          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(13,148,136,0.25)",border:"1px solid rgba(45,212,191,0.5)",padding:"6px 16px",borderRadius:20,fontSize:13,color:"#5EEAD4",marginBottom:24}}>
            <Icons.Shield/> Trusted Certification Body
          </div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(36px,5vw,64px)",fontWeight:800,lineHeight:1.1,marginBottom:20,color:"#F8FAFC"}}>
            Redefining<br/><span style={{color:"#2DD4BF"}}>Professional Trust</span>
          </h1>
          <p style={{fontSize:18,color:"#CBD5E1",maxWidth:560,margin:"0 auto 36px",lineHeight:1.7}}>
            We verify. We certify. We protect consumers from fraudsters and empower credible businesses to shine.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("certifications")} style={{fontSize:16,padding:"16px 32px"}}>Get Certified <Icons.ArrowRight/></button>
            <button className="nova-btn nova-btn-secondary" onClick={()=>onNavigate("verify")} style={{borderColor:"#5EEAD4",color:"#5EEAD4",fontSize:16,padding:"16px 32px"}}>Verify a Certificate</button>
          </div>
        </div>
      </section>
      <section style={{maxWidth:1100,margin:"0 auto",padding:"clamp(40px,7vw,80px) 16px"}}>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:36,textAlign:"center",marginBottom:12}}>How It Works</h2>
        <p style={{textAlign:"center",color:"#4B5563",marginBottom:56,fontSize:16}}>Our rigorous 40–45 day audit ensures only credible businesses get certified</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(220px,100%),1fr))",gap:24}}>
          {[
            {step:"01",title:"Apply Online",desc:"Choose your plan, fill in details, upload documents & pay the fee"},
            {step:"02",title:"Deep Audit",desc:"Our auditors verify documents, reviews, social proof & online presence over 40–45 days"},
            {step:"03",title:"Decision",desc:"Certificate issued with a unique verifiable number, or rejected with detailed comments"},
            {step:"04",title:"Public Listing",desc:"Your certification is publicly verifiable on our website for 1 year"},
          ].map(item=>(
            <div key={item.step} className="nova-card" style={{padding:32,textAlign:"center"}}>
              <div style={{fontSize:48,fontWeight:800,color:"#E7E5E4",fontFamily:"'Playfair Display',serif",marginBottom:12}}>{item.step}</div>
              <h3 style={{fontSize:18,fontWeight:700,marginBottom:8}}>{item.title}</h3>
              <p style={{fontSize:14,color:"#4B5563",lineHeight:1.6}}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PROFESSIONAL PRODUCT SECTION ── */}
      <section style={{background:"#0F172A",padding:"clamp(48px,8vw,80px) 24px"}}>
        <div style={{maxWidth:1100,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(300px,100%),1fr))",gap:40,alignItems:"center"}}>
          <div>
            <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(13,148,136,.2)",border:"1px solid rgba(13,148,136,.3)",padding:"5px 14px",borderRadius:20,fontSize:12,color:"#34D399",marginBottom:16,fontWeight:600}}>
              🆕 New Product
            </div>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(26px,4vw,42px)",fontWeight:700,color:"#fff",marginBottom:16,lineHeight:1.2}}>
              NovaCert Professional<br/>
              <span style={{color:"#0D9488"}}>CV Verification</span>
            </h2>
            <p style={{fontSize:15,color:"#94A3B8",lineHeight:1.8,marginBottom:28,maxWidth:480}}>
              India's fake CV problem costs companies crores every year. Candidates inflate experience, fabricate degrees, and invent employers — and HR teams have no easy way to verify before hiring.
            </p>
            <p style={{fontSize:15,color:"#CBD5E1",lineHeight:1.8,marginBottom:32,maxWidth:480}}>
              NovaCert Professional independently verifies a candidate's identity, education, employment history, skills, and references — so you know exactly who you are hiring.
            </p>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("professional")} style={{fontSize:15,padding:"14px 28px"}}>For Professionals →</button>
              <button className="nova-btn" onClick={()=>onNavigate("pro-verify")} style={{fontSize:15,padding:"14px 28px",background:"rgba(255,255,255,.08)",color:"#fff",border:"1px solid rgba(255,255,255,.2)"}}>Verify a Candidate</button>
            </div>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {[
              {icon:"🪪",title:"Identity",desc:"Aadhaar & PAN verified"},
              {icon:"🎓",title:"Education",desc:"Degree & institution confirmed"},
              {icon:"💼",title:"Employment",desc:"Employers independently contacted"},
              {icon:"📞",title:"References",desc:"Reference calls made directly"},
              {icon:"🛠",title:"Skills",desc:"Independently assessed"},
              {icon:"🔒",title:"Private",desc:"Not public — cert number only"},
            ].map(f=>(
              <div key={f.title} style={{background:"rgba(255,255,255,.05)",border:"1px solid rgba(255,255,255,.08)",borderRadius:10,padding:"16px 14px"}}>
                <div style={{fontSize:22,marginBottom:6}}>{f.icon}</div>
                <p style={{fontWeight:700,fontSize:13,color:"#fff",marginBottom:3}}>{f.title}</p>
                <p style={{fontSize:12,color:"#64748B"}}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR HR / RECRUITERS SECTION ── */}
      <section style={{background:"#F0FDFA",borderTop:"1px solid #CCFBF1",padding:"clamp(40px,7vw,72px) 24px"}}>
        <div style={{maxWidth:1000,margin:"0 auto",textAlign:"center",marginBottom:40}}>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(24px,3.5vw,38px)",fontWeight:700,color:"#0F172A",marginBottom:12}}>For HR Teams & Recruiters</h2>
          <p style={{color:"#4B5563",fontSize:15,maxWidth:560,margin:"0 auto"}}>Stop wasting time on fake CVs. Verify any candidate's NovaCert certificate in seconds — no login, no subscription required.</p>
        </div>
        <div style={{maxWidth:1000,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(220px,100%),1fr))",gap:20,marginBottom:40}}>
          {[
            {icon:"⚡",title:"Instant Verification",desc:"Enter the certificate number. See verified credentials in under 3 seconds."},
            {icon:"🔍",title:"Independent Audit",desc:"Every certificate is based on a 20-30 day independent verification — not self-reported."},
            {icon:"🔒",title:"Privacy Protected",desc:"Candidate details only visible with their certificate number. Never publicly listed."},
            {icon:"💰",title:"Free to Verify",desc:"Verification is completely free for HR teams and recruiters. No account needed."},
          ].map(f=>(
            <div key={f.title} className="nova-card" style={{padding:"22px 18px",textAlign:"center"}}>
              <div style={{fontSize:30,marginBottom:10}}>{f.icon}</div>
              <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:8}}>{f.title}</h3>
              <p style={{fontSize:13,color:"#4B5563",lineHeight:1.6}}>{f.desc}</p>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
          <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("pro-verify")} style={{fontSize:15,padding:"14px 32px"}}>Verify a Candidate Now →</button>
          <button className="nova-btn nova-btn-secondary" onClick={()=>onNavigate("professional")} style={{fontSize:15,padding:"14px 32px"}}>Learn About Professional Verification</button>
        </div>
      </section>

      <section style={{background:"#F0FDFA",padding:"64px 24px",textAlign:"center"}}>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:32,marginBottom:12}}>Ready to Build Trust?</h2>
        <p style={{color:"#4B5563",marginBottom:32,fontSize:16}}>Choose the certification that fits your business</p>
        <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("certifications")} style={{fontSize:16,padding:"16px 40px"}}>View Certification Plans <Icons.ArrowRight/></button>
      </section>
    </>
  );
}


// ═══════════════════════════════════════════
// ─── CERTIFICATIONS PAGE ─────────────────
// ═══════════════════════════════════════════
function CertificationsPage({ onSelectPlan }) {
  return (
    <section style={{maxWidth:1000,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>
      <div style={{textAlign:"center",marginBottom:56}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,42px)",marginBottom:12}}>Certification Plans</h1>
        <p style={{color:"#4B5563",fontSize:16}}>Choose the level of credibility verification your business needs</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(360px,100%),1fr))",gap:32}}>
        {Object.entries(PLANS).map(([key,plan])=>(
          <div key={key} className="nova-card" style={{position:"relative",border:key==="credseal"?"2px solid #7C3AED":undefined}}>
            {key==="credseal" && <div style={{position:"absolute",top:-1,left:"50%",transform:"translateX(-50%)",background:"#7C3AED",color:"#fff",padding:"4px 20px",borderRadius:"0 0 10px 10px",fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:1}}>Recommended</div>}
            <div style={{padding:"40px 32px 32px"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:20}}>
                <div style={{width:10,height:10,borderRadius:"50%",background:plan.color}}/>
                <span style={{fontSize:13,fontWeight:600,color:plan.color,textTransform:"uppercase",letterSpacing:1}}>{key==="credseal"?"Advanced + CredSeal™":"Professional"}</span>
              </div>
              <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,marginBottom:4}}>{plan.name}</h2>
              <div style={{marginTop:20,marginBottom:24}}>
                <span style={{fontSize:42,fontWeight:800}}>₹{plan.price.toLocaleString("en-IN")}</span>
                <span style={{color:"#4B5563",fontSize:14}}> + GST (₹{plan.gst.toFixed(0)})</span>
                <div style={{fontSize:13,color:"#6B7280",marginTop:4}}>Renewal: ₹{plan.renewal.toLocaleString("en-IN")}/year</div>
              </div>
              <div style={{marginBottom:28}}>
                {plan.features.map((f,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"6px 0",fontSize:14,color:"#44403C"}}>
                    <div style={{color:plan.color,flexShrink:0,marginTop:2}}><Icons.Check/></div>{f}
                  </div>
                ))}
              </div>
              <button className="nova-btn" onClick={()=>onSelectPlan(key)} style={{width:"100%",justifyContent:"center",background:plan.color,color:"#fff",fontSize:15}}>Apply Now <Icons.ArrowRight/></button>
            </div>
          </div>
        ))}
      </div>
      <div className="nova-card" style={{marginTop:48,padding:32,textAlign:"center"}}>
        <h3 style={{fontSize:18,fontWeight:700,marginBottom:8}}>What is CredSeal™?</h3>
        <p style={{color:"#4B5563",maxWidth:600,margin:"0 auto",fontSize:14,lineHeight:1.7}}>
          CredSeal™ is NovaCert's proprietary credibility standard, backed by a governing body. Think of it like ISO for service provider reliability. It tells the world that your business has been thoroughly vetted and meets the highest standards of trust.
        </p>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── SHARED FORM COMPONENTS ──────────────
// These MUST live outside ApplicationForm.
// Defining components inside a render function
// causes React to treat them as new types on
// every render → input loses focus after each keystroke.
// ═══════════════════════════════════════════
function InputField({ label, value, onChange, type="text", required=false, placeholder="", phoneOnly=false }) {
  const handleKey = phoneOnly
    ? (e) => { if (!/[\d+\s\-]/.test(e.key) && !["Backspace","Delete","Tab","ArrowLeft","ArrowRight"].includes(e.key)) e.preventDefault(); }
    : undefined;
  const handlePaste = phoneOnly
    ? (e) => { const text = e.clipboardData.getData("text"); if (!/^[\d+\s\-]+$/.test(text)) e.preventDefault(); }
    : undefined;
  return (
    <div style={{marginBottom:16}}>
      <label className="nova-label">{label} {required && <span style={{color:"#DC2626"}}>*</span>}</label>
      <input
        className="nova-input"
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        onKeyDown={handleKey}
        onPaste={handlePaste}
        pattern={type==="email" ? "[a-z0-9._%+\\-]+@[a-z0-9.\\-]+\\.[a-z]{2,}$" : undefined}
        inputMode={phoneOnly ? "numeric" : undefined}
      />
    </div>
  );
}

function FileUploadBox({ label, category, files, uploadingFiles, onUpload, accept="image/*,.pdf,.doc,.docx,.mp4,.mov", multiple=true }) {
  return (
    <div style={{marginBottom:16}}>
      <label className="nova-label">{label}</label>
      <label style={{display:"block",border:"2px dashed #D6D3D1",borderRadius:10,padding:24,textAlign:"center",cursor:"pointer",background:"#FAFAF9",transition:"border-color .2s"}}
        onMouseEnter={e=>e.currentTarget.style.borderColor="#0D9488"}
        onMouseLeave={e=>e.currentTarget.style.borderColor="#D6D3D1"}>
        <input type="file" accept={accept} multiple={multiple} onChange={e=>onUpload(e,category)} style={{display:"none"}}/>
        <div style={{color:"#6B7280",marginBottom:4,display:"flex",justifyContent:"center"}}>
          {uploadingFiles[category] ? <Icons.Loader/> : <Icons.Upload/>}
        </div>
        <p style={{fontSize:13,color:files[category].length?"#0D9488":"#4B5563",fontWeight:500}}>
          {uploadingFiles[category] ? "Uploading..." :
           files[category].length ? `${files[category].length} file(s) uploaded ✓` :
           "Click to upload files"}
        </p>
      </label>
      {files[category].length > 0 && (
        <div style={{marginTop:8}}>
          {files[category].map((f,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#374151",padding:"3px 0"}}>
              <Icons.FileText/> {f.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// ─── APPLICATION FORM (Firebase) ─────────
// ═══════════════════════════════════════════
function ApplicationForm({ plan, onSubmit, onBack }) {
  const planInfo = PLANS[plan];
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [form, setForm] = useState({
    fullName:"", email:"", phone:"", whatsapp:"",
    companyName:"", designation:"", industry:"", city:"", state:"", address:"", website:"", yearsInBusiness:"",
    gstNumber:"", panNumber:"",
    googleBusiness:"", facebook:"", instagram:"", linkedin:"", twitter:"", youtube:"",
    clientRef1Name:"", clientRef1Phone:"", clientRef1Email:"",
    clientRef2Name:"", clientRef2Phone:"", clientRef2Email:"",
    aboutBusiness:"",
    agreeTerms: false,
  });
  // File state: store download URLs after upload
  const [files, setFiles] = useState({ regDocs:[], officePhotos:[], portfolio:[] });

  // Stable field updater — does NOT recreate on each render
  const u = useCallback((field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(prev => ({...prev, [field]: val}));
  }, []);

  // ── File Upload Handler ──
  const handleFileUpload = async (e, category) => {
    const fileList = Array.from(e.target.files);
    if (!fileList.length) return;
    setUploadingFiles(prev => ({...prev, [category]: true}));
    const urls = [];
    for (const file of fileList) {
      try {
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const storageRef = ref(storage, `applications/${form.email || 'unknown'}/${category}/${timestamp}_${safeName}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        urls.push({ name: file.name, url, type: file.type });
      } catch (err) {
        console.error("Upload error:", err);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
    setFiles(prev => ({...prev, [category]: [...prev[category], ...urls]}));
    setUploadingFiles(prev => ({...prev, [category]: false}));
  };

  // ── Submit to Firestore (with timeout + localStorage fallback) ──
  const handleSubmit = async () => {
    setSubmitting(true);
    const appId = generateAppId();
    const payload = {
      ...form,
      appId,
      plan,
      status: "pending",
      submittedAt: new Date().toISOString(),
      certNumber: null,
      issuedAt: null,
      expiryDate: null,
      adminComments: "",
      files: {
        regDocs: files.regDocs,
        officePhotos: files.officePhotos,
        portfolio: files.portfolio,
      },
    };

    // Always save to localStorage first as instant backup
    try {
      const existing = JSON.parse(localStorage.getItem("nova_applications") || "[]");
      existing.push(payload);
      localStorage.setItem("nova_applications", JSON.stringify(existing));
    } catch(_) {}

    // Try Firestore with 8-second timeout — success either way
    const firestorePromise = addDoc(collection(db, "applications"), {
      ...payload,
      submittedAt: new Date(),
    });
    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), 8000)
    );

    try {
      await Promise.race([firestorePromise, timeout]);
    } catch (err) {
      // Firestore failed or timed out — data is safe in localStorage
      console.warn("Firestore save failed, data saved locally:", err.message);
    }

    // Always proceed to success
    onSubmit(appId, plan, { email: form.email, phone: form.phone, name: form.fullName });
  };

  const totalSteps = 5;
  const canProceed = () => {
    if(step===1) return form.fullName && form.email && form.phone;
    if(step===2) return form.companyName && form.industry && form.city && form.state;
    if(step===4) return form.clientRef1Name && form.clientRef1Phone;
    if(step===5) return form.agreeTerms;
    return true;
  };

  return (
    <section style={{maxWidth:720,margin:"0 auto",padding:"48px 24px"}}>
      <div style={{marginBottom:36}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"#4B5563",cursor:"pointer",fontSize:14,fontFamily:"'DM Sans'",marginBottom:16}}>← Back to Plans</button>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
          <div style={{width:10,height:10,borderRadius:"50%",background:planInfo.color}}/>
          <span style={{fontSize:13,fontWeight:600,color:planInfo.color,textTransform:"uppercase",letterSpacing:1}}>{plan==="credseal"?"Advanced + CredSeal™":"Professional"}</span>
        </div>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:32}}>Application Form</h1>
        <p style={{color:"#4B5563",fontSize:14,marginTop:4}}>Total: ₹{planInfo.total.toLocaleString("en-IN")} (incl. GST)</p>
      </div>
      <div style={{display:"flex",gap:4,marginBottom:36}}>
        {Array.from({length:totalSteps}).map((_,i)=>(
          <div key={i} style={{flex:1,height:4,borderRadius:2,background:i<step?planInfo.color:"#E7E5E4",transition:"background .3s"}}/>
        ))}
      </div>
      <div className="nova-card" style={{padding:"36px 32px"}}>

        {step===1 && (
          <div className="fade-in">
            <h2 style={{fontSize:20,fontWeight:700,marginBottom:24}}>Personal Details</h2>
            <InputField label="Full Name" value={form.fullName} onChange={u("fullName")} required placeholder="Your full name"/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="Email" value={form.email} onChange={u("email")} type="email" required placeholder="you@email.com"/>
              <InputField label="Phone" value={form.phone} onChange={u("phone")} type="tel" required placeholder="+91 98765 43210" phoneOnly/>
            </div>
            <InputField label="WhatsApp Number" value={form.whatsapp} onChange={u("whatsapp")} type="tel" placeholder="+91 98765 43210 (if different)" phoneOnly/>
          </div>
        )}

        {step===2 && (
          <div className="fade-in">
            <h2 style={{fontSize:20,fontWeight:700,marginBottom:24}}>Company / Business Details</h2>
            <InputField label="Company / Business Name" value={form.companyName} onChange={u("companyName")} required placeholder="Your business name"/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="Your Designation" value={form.designation} onChange={u("designation")} placeholder="Founder, CEO, etc."/>
              <InputField label="Industry" value={form.industry} onChange={u("industry")} required placeholder="e.g. Digital Marketing"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="City" value={form.city} onChange={u("city")} required placeholder="City"/>
              <InputField label="State" value={form.state} onChange={u("state")} required placeholder="State"/>
            </div>
            <InputField label="Business Address" value={form.address} onChange={u("address")} placeholder="Full address"/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="Website" value={form.website} onChange={u("website")} placeholder="https://..."/>
              <InputField label="Years in Business" value={form.yearsInBusiness} onChange={u("yearsInBusiness")} placeholder="e.g. 3"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="GST Number (if any)" value={form.gstNumber} onChange={u("gstNumber")} placeholder="Optional"/>
              <InputField label="PAN Number" value={form.panNumber} onChange={u("panNumber")} placeholder="ABCDE1234F"/>
            </div>
          </div>
        )}

        {step===3 && (
          <div className="fade-in">
            <h2 style={{fontSize:20,fontWeight:700,marginBottom:24}}>Online Presence & Documents</h2>
            <p style={{color:"#4B5563",fontSize:13,marginBottom:20}}>These help our auditors verify your digital footprint and legitimacy.</p>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:12,color:"#0D9488"}}>Social & Online Links</h3>
            <InputField label="Google Business Profile URL" value={form.googleBusiness} onChange={u("googleBusiness")} placeholder="https://g.co/..."/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="Facebook" value={form.facebook} onChange={u("facebook")} placeholder="URL"/>
              <InputField label="Instagram" value={form.instagram} onChange={u("instagram")} placeholder="URL"/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="LinkedIn" value={form.linkedin} onChange={u("linkedin")} placeholder="URL"/>
              <InputField label="Twitter / X" value={form.twitter} onChange={u("twitter")} placeholder="URL"/>
            </div>
            <InputField label="YouTube Channel" value={form.youtube} onChange={u("youtube")} placeholder="URL"/>
            <h3 style={{fontSize:15,fontWeight:600,margin:"28px 0 12px",color:"#0D9488"}}>Document Uploads</h3>
            <FileUploadBox label="Registration Documents (GST, Udyam, Incorporation, etc.)" category="regDocs" files={files} uploadingFiles={uploadingFiles} onUpload={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"/>
            <FileUploadBox label="Office / Workspace Photos" category="officePhotos" files={files} uploadingFiles={uploadingFiles} onUpload={handleFileUpload} accept="image/*"/>
            <FileUploadBox label="Portfolio / Work Samples / Videos" category="portfolio" files={files} uploadingFiles={uploadingFiles} onUpload={handleFileUpload} accept="image/*,.pdf,.mp4,.mov,.doc,.docx"/>
          </div>
        )}

        {step===4 && (
          <div className="fade-in">
            <h2 style={{fontSize:20,fontWeight:700,marginBottom:8}}>Client References</h2>
            <p style={{color:"#4B5563",fontSize:13,marginBottom:24}}>Our auditors will contact these references to verify work quality.</p>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:12,color:"#0D9488"}}>Reference 1 *</h3>
            <InputField label="Client Name" value={form.clientRef1Name} onChange={u("clientRef1Name")} required placeholder="Full name"/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="Phone" value={form.clientRef1Phone} onChange={u("clientRef1Phone")} type="tel" required placeholder="+91..." phoneOnly/>
              <InputField label="Email" value={form.clientRef1Email} onChange={u("clientRef1Email")} type="email" placeholder="Email"/>
            </div>
            <h3 style={{fontSize:15,fontWeight:600,margin:"24px 0 12px",color:"#0D9488"}}>Reference 2 (Optional)</h3>
            <InputField label="Client Name" value={form.clientRef2Name} onChange={u("clientRef2Name")} placeholder="Full name"/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
              <InputField label="Phone" value={form.clientRef2Phone} onChange={u("clientRef2Phone")} type="tel" placeholder="+91..." phoneOnly/>
              <InputField label="Email" value={form.clientRef2Email} onChange={u("clientRef2Email")} type="email" placeholder="Email"/>
            </div>
            <div style={{marginTop:24}}>
              <label className="nova-label">Tell Us About Your Business</label>
              <textarea className="nova-input" rows={4} value={form.aboutBusiness} onChange={u("aboutBusiness")} placeholder="Describe your business, specialties, target market, achievements..."/>
            </div>
          </div>
        )}

        {step===5 && (
          <div className="fade-in">
            <h2 style={{fontSize:20,fontWeight:700,marginBottom:24}}>Review & Submit</h2>
            <div style={{background:"#F0FDFA",borderRadius:12,padding:24,marginBottom:24}}>
              <h3 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Application Summary</h3>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 24px",fontSize:14}}>
                <div><span style={{color:"#4B5563"}}>Plan:</span> <strong>{planInfo.name}</strong></div>
                <div><span style={{color:"#4B5563"}}>Name:</span> {form.fullName}</div>
                <div><span style={{color:"#4B5563"}}>Company:</span> {form.companyName}</div>
                <div><span style={{color:"#4B5563"}}>Industry:</span> {form.industry}</div>
                <div><span style={{color:"#4B5563"}}>City:</span> {form.city}, {form.state}</div>
                <div><span style={{color:"#4B5563"}}>Email:</span> {form.email}</div>
                <div><span style={{color:"#4B5563"}}>Phone:</span> {form.phone}</div>
                <div><span style={{color:"#4B5563"}}>Docs:</span> {files.regDocs.length} | Photos: {files.officePhotos.length} | Portfolio: {files.portfolio.length}</div>
              </div>
            </div>
            <div style={{background:"#FFF7ED",borderRadius:12,padding:20,marginBottom:24,border:"1px solid #FED7AA",display:"flex",gap:12,alignItems:"flex-start"}}>
              <span style={{fontSize:20}}>📋</span>
              <div style={{fontSize:13,color:"#92400E",lineHeight:1.6}}>
                <strong>What happens next?</strong><br/>
                After you submit, our team will review your application and contact you on <strong>{form.phone || "your phone"}</strong> or <strong>{form.email || "your email"}</strong> to discuss the certification process and payment. A unique Application ID will be generated for your reference.
              </div>
            </div>
            <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",fontSize:13,color:"#374151",marginBottom:24}}>
              <input type="checkbox" checked={form.agreeTerms} onChange={u("agreeTerms")} style={{marginTop:2,width:18,height:18,accentColor:"#0D9488"}}/>
              <span>I confirm all information provided is accurate. I understand the audit takes 40–45 days and the certification decision is at NovaCert's discretion. I agree to the terms and conditions.</span>
            </label>
          </div>
        )}

        <div style={{display:"flex",justifyContent:"space-between",marginTop:28}}>
          {step>1 ? <button className="nova-btn nova-btn-secondary nova-btn-sm" onClick={()=>setStep(step-1)}>← Previous</button> : <div/>}
          {step<totalSteps ? (
            <button className="nova-btn nova-btn-primary nova-btn-sm" disabled={!canProceed()} onClick={()=>setStep(step+1)}>Next →</button>
          ) : (
            <button className="nova-btn nova-btn-primary" disabled={!canProceed()||submitting} onClick={handleSubmit} style={{background:planInfo.color}}>
              {submitting ? <><Icons.Loader/> Submitting...</> : <>Submit Application <Icons.ArrowRight/></>}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── SUCCESS PAGE ────────────────────────
// ═══════════════════════════════════════════
function SuccessPage({ appId, submission, onNavigate }) {
  const [copied, setCopied] = useState(false);
  const planInfo = submission ? PLANS[submission.planKey] : null;

  const copyId = () => {
    if (appId) {
      navigator.clipboard.writeText(appId).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <section style={{maxWidth:640,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px clamp(40px,7vw,80px)",textAlign:"center"}}>
      <div className="slide-up">

        {/* Tick */}
        <div style={{width:88,height:88,borderRadius:"50%",background:"linear-gradient(135deg,#D1FAE5,#A7F3D0)",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 28px",boxShadow:"0 8px 32px rgba(16,185,129,0.2)"}}>
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
        </div>

        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:36,marginBottom:8,color:"#0F172A"}}>Application Received!</h1>
        <p style={{color:"#4B5563",fontSize:16,marginBottom:36}}>Your application has been successfully submitted.</p>

        {/* App ID Card */}
        <div style={{background:"#0F172A",borderRadius:16,padding:"28px 32px",marginBottom:24,position:"relative",overflow:"hidden"}}>
          <div style={{position:"absolute",top:-40,right:-40,width:160,height:160,borderRadius:"50%",background:"rgba(13,148,136,0.1)"}}/>
          <p style={{color:"#94A3B8",fontSize:12,textTransform:"uppercase",letterSpacing:2,marginBottom:8,fontWeight:600}}>Your Application ID</p>
          <div style={{fontFamily:"'Courier New',monospace",fontSize:28,fontWeight:800,color:"#2DD4BF",letterSpacing:2,marginBottom:16}}>
            {appId || "NCA-2026-XXXXXX"}
          </div>
          <button onClick={copyId} style={{background:"rgba(13,148,136,0.2)",border:"1px solid rgba(45,212,191,0.3)",color:"#2DD4BF",padding:"8px 20px",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,fontFamily:"'DM Sans',sans-serif"}}>
            {copied ? "✓ Copied!" : "Copy ID"}
          </button>
          <p style={{color:"#64748B",fontSize:12,marginTop:12}}>Save this ID — you'll need it to track your application</p>
        </div>

        {/* Amount & Contact Info */}
        <div style={{background:"#fff",border:"1px solid #D6D3D1",borderRadius:16,padding:"24px 28px",marginBottom:24,textAlign:"left"}}>
          {planInfo && (
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",paddingBottom:16,marginBottom:16,borderBottom:"1px solid #F5F5F4"}}>
              <div>
                <p style={{fontSize:13,color:"#6B7280",marginBottom:2}}>Selected Plan</p>
                <p style={{fontWeight:700,color:"#0F172A",fontSize:15}}>{planInfo.name}</p>
              </div>
              <div style={{textAlign:"right"}}>
                <p style={{fontSize:13,color:"#6B7280",marginBottom:2}}>Total Amount</p>
                <p style={{fontWeight:800,fontSize:22,color:"#0D9488"}}>₹{planInfo.total.toLocaleString("en-IN")}</p>
                <p style={{fontSize:11,color:"#9CA3AF"}}>incl. GST</p>
              </div>
            </div>
          )}
          <div style={{display:"flex",gap:12,alignItems:"flex-start"}}>
            <div style={{width:36,height:36,borderRadius:"50%",background:"#FEF3C7",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:18}}>📞</div>
            <div>
              <p style={{fontWeight:700,color:"#0F172A",fontSize:15,marginBottom:4}}>We'll contact you within 24 working hours</p>
              <p style={{fontSize:13,color:"#4B5563",lineHeight:1.6}}>
                Our team will reach out to you
                {submission?.formData?.phone ? <strong> on {submission.formData.phone}</strong> : ""}
                {submission?.formData?.email ? <> or <strong>{submission.formData.email}</strong></> : ""}
                {" "}to discuss the certification process and collect the payment of{planInfo ? <strong> ₹{planInfo.total.toLocaleString("en-IN")}</strong> : " the fee"}.
              </p>
            </div>
          </div>
        </div>

        {/* What's next */}
        <div style={{background:"#F0FDFA",borderRadius:12,padding:"16px 20px",marginBottom:32,textAlign:"left"}}>
          <p style={{fontSize:13,fontWeight:700,color:"#0D9488",marginBottom:8}}>What happens next?</p>
          <div style={{fontSize:13,color:"#374151",lineHeight:1.8}}>
            1. Our team contacts you within 24 working hours<br/>
            2. Payment is collected &amp; confirmed<br/>
            3. Audit begins (40–45 days)<br/>
            4. Certificate issued with unique cert number
          </div>
        </div>

        <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("home")} style={{width:"100%",justifyContent:"center",fontSize:16}}>
          Back to Home
        </button>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── CONTACT PAGE ────────────────────────
// ═══════════════════════════════════════════
function ContactPage() {
  return (
    <section style={{maxWidth:700,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>
      <div style={{textAlign:"center",marginBottom:52}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,42px)",marginBottom:12}}>Contact Us</h1>
        <p style={{color:"#4B5563",fontSize:16}}>We'd love to hear from you. Reach out anytime.</p>
      </div>

      <div style={{display:"flex",flexDirection:"column",gap:20,maxWidth:480,margin:"0 auto"}}>
        {[
          { icon:"✉", label:"Email", value:"info@novacert.in", href:"mailto:info@novacert.in" },
          { icon:"📞", label:"Phone / WhatsApp", value:"+91 91150 19666", href:"tel:+919115019666" },
          { icon:"🕐", label:"Working Hours", value:"Mon–Sat, 10am – 6pm IST" },
        ].map(item => (
          <div key={item.label} className="nova-card" style={{padding:"24px 28px",display:"flex",gap:20,alignItems:"center"}}>
            <div style={{fontSize:32,lineHeight:1,flexShrink:0}}>{item.icon}</div>
            <div>
              <p style={{fontSize:12,color:"#6B7280",fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>{item.label}</p>
              {item.href
                ? <a href={item.href} style={{fontSize:17,fontWeight:700,color:"#0D9488",textDecoration:"none"}}>{item.value}</a>
                : <p style={{fontSize:17,fontWeight:700,color:"#0F172A"}}>{item.value}</p>
              }
            </div>
          </div>
        ))}

        <div className="nova-card" style={{padding:"24px 28px",background:"#F0FDFA",border:"1px solid #99F6E4"}}>
          <p style={{fontSize:13,color:"#0D9488",fontWeight:700,marginBottom:6}}>💡 Fastest way to reach us</p>
          <p style={{fontSize:14,color:"#374151",lineHeight:1.7}}>
            WhatsApp us directly on <strong>+91 91150 19666</strong> with your business name and query — we typically respond within a few hours.
          </p>
        </div>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── EMPANELMENT PAGE ────────────────────
// ═══════════════════════════════════════════
function EmpanelPage({ onNavigate }) {
  const [form, setForm] = useState({
    name:"", email:"", phone:"", address:"", city:"", state:"",
    role:"", yearsExp:"", specialization:"", linkedin:"",
    education:"", currentOrg:"", whyNovacert:"", availability:"",
  });
  const [cvFile, setCvFile] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const u = useCallback((field) => (e) => setForm(prev => ({...prev, [field]: e.target.value})), []);

  const handlePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone || !form.role || !form.yearsExp) {
      setError("Please fill all required fields."); return;
    }
    if (!cvFile) { setError("Please upload your CV."); return; }
    if (!photoFile) { setError("Please upload your passport size photo."); return; }
    setSubmitting(true); setError("");

    let cvUrl = "", photoUrl = "";

    // Upload files to Firebase Storage
    try {
      setUploading(true);
      const ts = Date.now();
      const cvRef = ref(storage, `empanelment/${form.email}/cv_${ts}_${cvFile.name}`);
      await uploadBytes(cvRef, cvFile);
      cvUrl = await getDownloadURL(cvRef);

      const photoRef = ref(storage, `empanelment/${form.email}/photo_${ts}_${photoFile.name}`);
      await uploadBytes(photoRef, photoFile);
      photoUrl = await getDownloadURL(photoRef);
      setUploading(false);
    } catch(err) {
      console.warn("File upload failed:", err.message);
      setUploading(false);
    }

    // Save to Firestore
    try {
      await addDoc(collection(db, "empanelment"), {
        ...form, cvUrl, photoUrl,
        status: "pending_review",
        submittedAt: new Date(),
      });
    } catch(err) { console.warn("Firestore save failed:", err.message); }

    // Send email via Web3Forms
    try {
      await sendEmail(`[NovaCert Empanelment] ${form.role.toUpperCase()} Application — ${form.name}`, [
        `===== EMPANELMENT APPLICATION =====`,
        ``,
        `ROLE APPLIED FOR: ${form.role.toUpperCase()}`,
        ``,
        `--- Personal Details ---`,
        `Name:        ${form.name}`,
        `Email:       ${form.email}`,
        `Phone:       ${form.phone}`,
        `Address:     ${form.address}, ${form.city}, ${form.state}`,
        ``,
        `--- Professional Details ---`,
        `Years of Experience:  ${form.yearsExp}`,
        `Specialization:       ${form.specialization}`,
        `Education:            ${form.education}`,
        `Current Organisation: ${form.currentOrg || "Not provided"}`,
        `LinkedIn:             ${form.linkedin || "Not provided"}`,
        `Availability:         ${form.availability}`,
        ``,
        `--- Assessment ---`,
        `Why NovaCert:`,
        form.whyNovacert,
        ``,
        `--- Uploaded Files ---`,
        `CV:    ${cvUrl || "Upload failed — check Firebase Storage"}`,
        `Photo: ${photoUrl || "Upload failed — check Firebase Storage"}`,
        ``,
        `===================================`,
      ]);
    } catch(err) { console.warn("Email failed:", err.message); }

    setSubmitting(false);
    onNavigate("empanel-success");
  };

  const roleColor = form.role === "auditor" ? "#7C3AED" : "#0D9488";

  return (
    <section style={{maxWidth:760,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>

      {/* Header */}
      <div style={{textAlign:"center",marginBottom:48}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"#F0FDFA",border:"1px solid #99F6E4",padding:"6px 16px",borderRadius:20,fontSize:13,color:"#0D9488",marginBottom:16,fontWeight:600}}>
          🤝 Join the NovaCert Team
        </div>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,40px)",marginBottom:12}}>Get Empanelled</h1>
        <p style={{color:"#4B5563",fontSize:15,maxWidth:540,margin:"0 auto",lineHeight:1.7}}>
          We're always looking for experienced professionals to join our panel of auditors and consultants. Apply below — our team reviews every application carefully.
        </p>
      </div>

      <div className="nova-card" style={{padding:"40px 36px"}}>

        {/* Role Selection */}
        <div style={{marginBottom:28}}>
          <label className="nova-label">I am applying as <span style={{color:"#DC2626"}}>*</span></label>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(220px,100%),1fr))",gap:12,marginTop:6}}>
            {[["consultant","Consultant","Help businesses prepare & improve"],["auditor","Auditor","Evaluate & verify business applications"]].map(([val,title,desc])=>(
              <div key={val} onClick={()=>setForm(prev=>({...prev,role:val}))} style={{border:`2px solid ${form.role===val?(val==="auditor"?"#7C3AED":"#0D9488"):"#D6D3D1"}`,borderRadius:12,padding:"16px 20px",cursor:"pointer",background:form.role===val?(val==="auditor"?"#F5F3FF":"#F0FDFA"):"#fff",transition:"all .2s"}}>
                <p style={{fontWeight:700,fontSize:15,color:form.role===val?(val==="auditor"?"#7C3AED":"#0D9488"):"#1C1917",marginBottom:4}}>{title}</p>
                <p style={{fontSize:12,color:"#6B7280"}}>{desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Personal Info */}
        <h3 style={{fontSize:15,fontWeight:700,color:"#0F172A",marginBottom:16,paddingBottom:8,borderBottom:"1px solid #F5F5F4"}}>Personal Information</h3>
        <InputField label="Full Name" value={form.name} onChange={u("name")} required placeholder="As per official documents"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
          <InputField label="Email" value={form.email} onChange={u("email")} type="email" required placeholder="your@email.com"/>
          <InputField label="Phone / WhatsApp" value={form.phone} onChange={u("phone")} type="tel" required placeholder="+91..." phoneOnly/>
        </div>
        <InputField label="Full Address" value={form.address} onChange={u("address")} required placeholder="House/Office no, Street, Area"/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
          <InputField label="City" value={form.city} onChange={u("city")} required placeholder="City"/>
          <InputField label="State" value={form.state} onChange={u("state")} required placeholder="State"/>
        </div>

        {/* Passport Photo */}
        <div style={{marginBottom:20}}>
          <label className="nova-label">Passport Size Photo <span style={{color:"#DC2626"}}>*</span></label>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <div style={{width:80,height:80,borderRadius:10,border:"2px dashed #D6D3D1",overflow:"hidden",flexShrink:0,background:"#F9FAFB",display:"flex",alignItems:"center",justifyContent:"center"}}>
              {photoPreview ? <img src={photoPreview} alt="preview" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{fontSize:28}}>👤</span>}
            </div>
            <label style={{flex:1,border:"2px dashed #D6D3D1",borderRadius:10,padding:"14px 20px",cursor:"pointer",background:"#FAFAF9",textAlign:"center",fontSize:13,color:"#4B5563",fontWeight:500}}
              onMouseEnter={e=>e.currentTarget.style.borderColor="#0D9488"}
              onMouseLeave={e=>e.currentTarget.style.borderColor="#D6D3D1"}>
              <input type="file" accept="image/*" onChange={handlePhoto} style={{display:"none"}}/>
              {photoFile ? `✓ ${photoFile.name}` : "Click to upload photo (JPG/PNG)"}
            </label>
          </div>
        </div>

        {/* Professional Info */}
        <h3 style={{fontSize:15,fontWeight:700,color:"#0F172A",margin:"24px 0 16px",paddingBottom:8,borderBottom:"1px solid #F5F5F4"}}>Professional Background</h3>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16}}>
          <InputField label="Years of Experience" value={form.yearsExp} onChange={u("yearsExp")} required placeholder="e.g. 8"/>
          <InputField label="Current Organisation" value={form.currentOrg} onChange={u("currentOrg")} placeholder="Company / Self-employed"/>
        </div>
        <InputField label="Area of Specialization" value={form.specialization} onChange={u("specialization")} required placeholder="e.g. Quality Assurance, Business Audit, ISO..."/>
        <InputField label="Highest Education / Qualification" value={form.education} onChange={u("education")} required placeholder="e.g. MBA, CA, B.Tech..."/>
        <InputField label="LinkedIn Profile URL" value={form.linkedin} onChange={u("linkedin")} placeholder="https://linkedin.com/in/yourprofile"/>

        {/* CV Upload */}
        <div style={{marginBottom:20}}>
          <label className="nova-label">Upload CV / Resume <span style={{color:"#DC2626"}}>*</span></label>
          <label style={{display:"block",border:"2px dashed #D6D3D1",borderRadius:10,padding:"20px",textAlign:"center",cursor:"pointer",background:"#FAFAF9",transition:"border-color .2s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor="#0D9488"}
            onMouseLeave={e=>e.currentTarget.style.borderColor="#D6D3D1"}>
            <input type="file" accept=".pdf,.doc,.docx" onChange={e=>setCvFile(e.target.files[0])} style={{display:"none"}}/>
            <div style={{fontSize:24,marginBottom:6}}>📄</div>
            <p style={{fontSize:13,color:cvFile?"#0D9488":"#4B5563",fontWeight:500}}>
              {cvFile ? `✓ ${cvFile.name}` : "Click to upload CV (PDF or Word)"}
            </p>
          </label>
        </div>

        {/* Assessment Questions */}
        <h3 style={{fontSize:15,fontWeight:700,color:"#0F172A",margin:"24px 0 16px",paddingBottom:8,borderBottom:"1px solid #F5F5F4"}}>Assessment Questions</h3>
        <div style={{marginBottom:16}}>
          <label className="nova-label">Why do you want to work with NovaCert? <span style={{color:"#DC2626"}}>*</span></label>
          <textarea className="nova-input" rows={4} value={form.whyNovacert} onChange={u("whyNovacert")} placeholder="Tell us about your motivation, what you bring to the team, and how you align with our values of trust and excellence..."/>
        </div>
        <div style={{marginBottom:24}}>
          <label className="nova-label">Availability <span style={{color:"#DC2626"}}>*</span></label>
          <select className="nova-input" value={form.availability} onChange={u("availability")} style={{cursor:"pointer"}}>
            <option value="">Select availability</option>
            <option value="full-time">Full-time</option>
            <option value="part-time">Part-time (weekends)</option>
            <option value="freelance">Freelance / Per-project</option>
            <option value="consulting">Consulting basis</option>
          </select>
        </div>

        {/* Info box */}
        <div style={{background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:10,padding:"14px 18px",marginBottom:24,fontSize:13,color:"#92400E",lineHeight:1.6}}>
          <strong>📋 What happens after you apply?</strong><br/>
          Our team reviews every application carefully. Shortlisted candidates will be contacted within 5–7 working days for a brief telephonic interview.
        </div>

        {error && <p style={{color:"#DC2626",fontSize:13,marginBottom:12,fontWeight:500}}>{error}</p>}

        <button className="nova-btn nova-btn-primary" disabled={submitting} onClick={handleSubmit} style={{width:"100%",justifyContent:"center",fontSize:15,background:roleColor}}>
          {submitting ? <><Icons.Loader/> {uploading?"Uploading files...":"Submitting..."}</> : "Submit Application 🚀"}
        </button>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── EMPANEL SUCCESS PAGE ────────────────
// ═══════════════════════════════════════════
function EmpanelSuccessPage({ onNavigate }) {
  return (
    <section style={{maxWidth:560,margin:"0 auto",padding:"clamp(40px,7vw,80px) 16px",textAlign:"center"}}>
      <div className="slide-up">
        <div style={{fontSize:72,marginBottom:24}}>🎉</div>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:34,marginBottom:12}}>Application Received!</h1>
        <p style={{color:"#4B5563",fontSize:16,lineHeight:1.7,marginBottom:8}}>Thank you for your interest in joining the NovaCert panel.</p>
        <div style={{background:"#F0FDFA",border:"1px solid #99F6E4",borderRadius:12,padding:"20px 24px",margin:"24px 0",textAlign:"left"}}>
          <p style={{fontSize:14,color:"#0D9488",fontWeight:700,marginBottom:8}}>What's next?</p>
          <p style={{fontSize:14,color:"#374151",lineHeight:1.7}}>Our team will review your application and CV carefully. Shortlisted candidates will be contacted within <strong>5–7 working days</strong> for a brief telephonic interview.</p>
        </div>
        <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap",marginTop:8}}>
          <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("home")}>Back to Home</button>
          <button className="nova-btn nova-btn-secondary" onClick={()=>onNavigate("contact")}>Contact Us</button>
        </div>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── VERIFY PAGE (queries Firestore) ─────
// ═══════════════════════════════════════════
function VerifyPage({ result, setResult }) {
  const [queryStr, setQueryStr] = useState("");
  const [searching, setSearching] = useState(false);

  const search = async () => {
    const q = queryStr.trim().toUpperCase();
    if (!q) return;
    setSearching(true);
    try {
      const snap = await getDocs(
        query(collection(db, "applications"), where("certNumber", "==", q))
      );
      if (!snap.empty) {
        setResult({ id: snap.docs[0].id, ...snap.docs[0].data() });
      } else {
        setResult("not_found");
      }
    } catch (err) {
      console.error("Verify error:", err);
      alert("Error searching. Please try again.");
    }
    setSearching(false);
  };

  return (
    <section style={{maxWidth:700,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>
      <div style={{textAlign:"center",marginBottom:48}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,42px)",marginBottom:12}}>Verify Certificate</h1>
        <p style={{color:"#4B5563",fontSize:16}}>Enter the certificate number to verify its authenticity</p>
      </div>
      <div className="nova-card" style={{padding:36}}>
        <div style={{display:"flex",gap:12}}>
          <input className="nova-input" value={queryStr} onChange={e=>setQueryStr(e.target.value)} placeholder="e.g. NC-PC-2026-A1B2C3" onKeyDown={e=>e.key==="Enter"&&search()} style={{flex:1,fontSize:16}}/>
          <button className="nova-btn nova-btn-primary" onClick={search} disabled={searching}>
            {searching ? <Icons.Loader/> : <Icons.Search/>} Verify
          </button>
        </div>

        {result && result !== "not_found" && (
          <div className="fade-in" style={{marginTop:32}}>
            {(()=>{
              const expired = isExpired(result.expiryDate);
              const planInfo = PLANS[result.plan];
              return (
                <div style={{border:`2px solid ${expired?"#F59E0B":"#10B981"}`,borderRadius:12,padding:28}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
                    <div style={{width:48,height:48,borderRadius:"50%",background:expired?"#FEF3C7":"#D1FAE5",display:"flex",alignItems:"center",justifyContent:"center",color:expired?"#92400E":"#059669"}}>
                      {expired ? <Icons.AlertTriangle/> : <Icons.Check/>}
                    </div>
                    <div>
                      <h3 style={{fontSize:20,fontWeight:700,color:expired?"#92400E":"#059669"}}>{expired ? "Certificate Expired" : "Certificate Valid ✓"}</h3>
                      <p style={{fontSize:13,color:"#4B5563"}}>{result.certNumber}</p>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"12px 24px",fontSize:14}}>
                    <div><span style={{color:"#4B5563"}}>Business:</span> <strong>{result.companyName}</strong></div>
                    <div><span style={{color:"#4B5563"}}>Holder:</span> {result.fullName}</div>
                    <div><span style={{color:"#4B5563"}}>Industry:</span> {result.industry}</div>
                    <div><span style={{color:"#4B5563"}}>Location:</span> {result.city}, {result.state}</div>
                    <div><span style={{color:"#4B5563"}}>Plan:</span> <span style={{color:planInfo?.color}}>{planInfo?.badge}</span></div>
                    <div><span style={{color:"#4B5563"}}>Issued:</span> {formatDate(result.issuedAt)}</div>
                    <div style={{gridColumn:"1 / -1"}}>
                      <span style={{color:"#4B5563"}}>Valid Until:</span>{" "}
                      <strong style={{color:expired?"#DC2626":"#059669"}}>{formatDate(result.expiryDate)} {expired && " — EXPIRED"}</strong>
                    </div>
                  </div>
                  {result.plan==="credseal" && !expired && (
                    <div style={{marginTop:16,background:"linear-gradient(135deg,#7C3AED15,#7C3AED08)",border:"1px solid #7C3AED30",borderRadius:8,padding:"10px 16px",display:"flex",alignItems:"center",gap:8,fontSize:13,color:"#7C3AED",fontWeight:600}}>
                      <Icons.Star/> CredSeal™ Certified — Backed by Governing Body
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {result === "not_found" && (
          <div className="fade-in" style={{marginTop:32,textAlign:"center",padding:32,background:"#FEF2F2",borderRadius:12}}>
            <div style={{color:"#DC2626",marginBottom:8,display:"flex",justifyContent:"center"}}><Icons.AlertTriangle/></div>
            <h3 style={{fontSize:18,fontWeight:700,color:"#991B1B",marginBottom:4}}>Certificate Not Found</h3>
            <p style={{fontSize:14,color:"#4B5563"}}>No certificate matches this number. Please double-check and try again.</p>
          </div>
        )}
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── BLOG PAGE (Public) ──────────────────
// ═══════════════════════════════════════════
function BlogPage({ onNavigate, onOpenPost }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const q = query(
      collection(db, "blogs"),
      where("published", "==", true),
      orderBy("publishedAt", "desc")
    );
    const unsub = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const categories = ["All", ...Array.from(new Set(posts.map(p => p.category).filter(Boolean)))];
  const filtered = activeCategory === "All" ? posts : posts.filter(p => p.category === activeCategory);

  return (
    <section style={{maxWidth:1100,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>
      {/* Header */}
      <div style={{textAlign:"center",marginBottom:52}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,44px)",marginBottom:12}}>NovaCert Blog</h1>
        <p style={{color:"#4B5563",fontSize:16,maxWidth:520,margin:"0 auto"}}>Insights on business trust, certification, and professional excellence</p>
      </div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center",marginBottom:40}}>
          {categories.map(cat => (
            <button key={cat} onClick={()=>setActiveCategory(cat)} style={{padding:"6px 18px",borderRadius:20,border:"2px solid",borderColor:activeCategory===cat?"#0D9488":"#D6D3D1",background:activeCategory===cat?"#0D9488":"#fff",color:activeCategory===cat?"#fff":"#374151",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'DM Sans',sans-serif",transition:"all .2s"}}>
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Posts Grid */}
      {loading ? (
        <div style={{textAlign:"center",padding:60,color:"#6B7280"}}><Icons.Loader/></div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:80}}>
          <div style={{fontSize:48,marginBottom:16}}>✍️</div>
          <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:24,marginBottom:8}}>No posts yet</h3>
          <p style={{color:"#6B7280",fontSize:15}}>Check back soon — fresh content coming.</p>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:28}}>
          {filtered.map(post => (
            <article key={post.id} className="nova-card" onClick={()=>onOpenPost(post)} style={{cursor:"pointer",display:"flex",flexDirection:"column"}}>
              {post.coverImage && (
                <div style={{height:200,overflow:"hidden"}}>
                  <img src={post.coverImage} alt={post.title} style={{width:"100%",height:"100%",objectFit:"cover",transition:"transform .3s"}}
                    onMouseEnter={e=>e.target.style.transform="scale(1.05)"}
                    onMouseLeave={e=>e.target.style.transform="scale(1)"}/>
                </div>
              )}
              <div style={{padding:"24px 24px 20px",flex:1,display:"flex",flexDirection:"column"}}>
                {post.category && (
                  <span style={{fontSize:11,fontWeight:700,color:"#0D9488",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>{post.category}</span>
                )}
                <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:700,marginBottom:10,lineHeight:1.3,color:"#0F172A"}}>{post.title}</h2>
                <p style={{fontSize:13,color:"#4B5563",lineHeight:1.6,flex:1,marginBottom:16}}>
                  {post.excerpt || post.content?.substring(0,120).replace(/<[^>]*>/g,"") + "..."}
                </p>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,color:"#6B7280",borderTop:"1px solid #F5F5F4",paddingTop:12}}>
                  <span>✍️ {post.author || "NovaCert Team"}</span>
                  <span>📅 {formatDate(post.publishedAt)}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── BLOG POST PAGE ──────────────────────
// ═══════════════════════════════════════════
function BlogPostPage({ post, onNavigate }) {
  if (!post) { onNavigate("blog"); return null; }
  return (
    <section style={{maxWidth:760,margin:"0 auto",padding:"48px 24px 80px"}}>
      <button onClick={()=>onNavigate("blog")} style={{background:"none",border:"none",color:"#6B7280",cursor:"pointer",fontSize:14,fontFamily:"'DM Sans'",marginBottom:24}}>← Back to Blog</button>

      {post.coverImage && (
        <div style={{borderRadius:16,overflow:"hidden",marginBottom:32,maxHeight:400}}>
          <img src={post.coverImage} alt={post.title} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
        </div>
      )}

      {post.category && (
        <span style={{fontSize:11,fontWeight:700,color:"#0D9488",textTransform:"uppercase",letterSpacing:1}}>{post.category}</span>
      )}

      <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(26px,4vw,40px)",fontWeight:800,margin:"12px 0 16px",lineHeight:1.2,color:"#0F172A"}}>{post.title}</h1>

      <div style={{display:"flex",gap:20,fontSize:13,color:"#6B7280",marginBottom:32,paddingBottom:24,borderBottom:"1px solid #E7E5E4"}}>
        <span>✍️ {post.author || "NovaCert Team"}</span>
        <span>📅 {formatDate(post.publishedAt)}</span>
        {post.readTime && <span>⏱ {post.readTime} min read</span>}
      </div>

      {/* Render content — supports plain text with line breaks */}
      <div style={{fontSize:16,lineHeight:1.9,color:"#1C1917"}}>
        {post.content?.split("\n").map((para, i) =>
          para.trim() === "" ? <br key={i}/> :
          para.startsWith("## ") ? <h2 key={i} style={{fontFamily:"'Playfair Display',serif",fontSize:24,fontWeight:700,margin:"32px 0 12px",color:"#0F172A"}}>{para.replace("## ","")}</h2> :
          para.startsWith("# ")  ? <h1 key={i} style={{fontFamily:"'Playfair Display',serif",fontSize:30,fontWeight:800,margin:"36px 0 14px",color:"#0F172A"}}>{para.replace("# ","")}</h1> :
          para.startsWith("- ")  ? <li key={i} style={{marginLeft:20,marginBottom:6,color:"#374151"}}>{para.replace("- ","")}</li> :
          <p key={i} style={{marginBottom:16,color:"#374151"}}>{para}</p>
        )}
      </div>

      {/* CTA */}
      <div style={{background:"linear-gradient(135deg,#0F172A,#1E293B)",borderRadius:16,padding:"32px",marginTop:48,textAlign:"center",color:"#fff"}}>
        <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:22,marginBottom:8}}>Ready to Get Certified?</h3>
        <p style={{color:"#94A3B8",fontSize:14,marginBottom:20}}>Join businesses and freelancers who've built trust with NovaCert.</p>
        <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("certifications")}>View Certification Plans →</button>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── ADMIN LOGIN (Firebase Auth) ─────────
// ═══════════════════════════════════════════
function AdminLogin({ authChecked }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !pass) return;
    setLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, pass);
    } catch (err) {
      console.error("Login error:", err);
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setError("Invalid email or password.");
      } else if (err.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please wait and try again.");
      } else {
        setError("Login failed. Please try again.");
      }
    }
    setLoading(false);
  };

  if (!authChecked) {
    return <div style={{textAlign:"center",padding:100,color:"#4B5563"}}><Icons.Loader/> Checking authentication...</div>;
  }

  return (
    <section style={{maxWidth:420,margin:"0 auto",padding:"100px 24px",textAlign:"center"}}>
      <div style={{width:64,height:64,borderRadius:16,background:"#0F172A",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 24px",color:"#0D9488"}}><Icons.Shield/></div>
      <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:28,marginBottom:8}}>Admin Access</h2>
      <p style={{color:"#4B5563",fontSize:14,marginBottom:28}}>Login with your admin credentials</p>
      <div>
        <input className="nova-input" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Admin email" style={{marginBottom:12,textAlign:"center",fontSize:15}}/>
        <input className="nova-input" type="password" value={pass} onChange={e=>setPass(e.target.value)} placeholder="Password" onKeyDown={e=>e.key==="Enter"&&handleLogin(e)} style={{marginBottom:12,textAlign:"center",fontSize:15}}/>
        {error && <p style={{color:"#DC2626",fontSize:13,marginBottom:12}}>{error}</p>}
        <button className="nova-btn nova-btn-dark" onClick={handleLogin} disabled={loading} style={{width:"100%",justifyContent:"center"}}>
          {loading ? <><Icons.Loader/> Logging in...</> : "Login"}
        </button>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── ADMIN DASHBOARD (Firestore) ─────────
// ═══════════════════════════════════════════
function AdminDashboard({ viewing, onView }) {
  const [adminTab, setAdminTab] = useState("applications");
  const [applications, setApplications] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [comments, setComments] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // ── Real-time listener for all applications ──
  useEffect(() => {
    const q = query(collection(db, "applications"), orderBy("submittedAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      const apps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setApplications(apps);
      setLoading(false);
    }, (err) => {
      console.error("Firestore listen error:", err);
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Real-time listener for professional applications ──
  useEffect(() => {
    const q = query(collection(db, "professionals"), orderBy("submittedAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setProfessionals(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error("Pro listener error:", err));
    return unsub;
  }, []);

  // ── Update professional in Firestore ──
  const updateProfessional = async (id, updates) => {
    try {
      await updateDoc(doc(db, "professionals", id), updates);
    } catch(err) { alert("Update failed: " + err.message); }
  };

  const issueProfCert = async (pro) => {
    const certNum = "NC-PRO-" + new Date().getFullYear() + "-" + Math.random().toString(36).substr(2,6).toUpperCase();
    const now = new Date();
    const expiry = new Date(now); expiry.setFullYear(expiry.getFullYear()+1);
    await updateProfessional(pro.id, {
      certNumber: certNum,
      status: "approved",
      issuedAt: now,
      expiryDate: expiry,
      educationVerified: true,
      employmentVerified: true,
      skillsVerified: pro.plan === "advanced",
      referencesVerified: true,
      complaintsCleared: true,
    });
    alert("Certificate issued: " + certNum);
  };

  // ── Update application in Firestore ──
  const updateApplication = async (id, updates) => {
    setActionLoading(true);
    try {
      const appRef = doc(db, "applications", id);
      const finalUpdates = { ...updates };
      const app = applications.find(a => a.id === id);

      if (updates.status === "approved") {
        if (app && !app.certNumber) {
          const certNum = generateCertNumber(app.plan);
          const now = new Date();
          finalUpdates.certNumber = certNum;
          finalUpdates.issuedAt = now;
          finalUpdates.expiryDate = getExpiryDate(now);

          // ── Auto email applicant on approval ──
          try {
            const planInfo = PLANS[app.plan];
            const expiryStr = getExpiryDate(now).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"});
            await fetch(MAILER_URL, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                to: app.email,
                subject: `🎉 Congratulations! Your NovaCert Certificate is Issued — ${certNum}`,
                message: [
                  `Dear ${app.fullName},`,
                  ``,
                  `Congratulations! We are delighted to inform you that your application for NovaCert ${planInfo?.name} has been APPROVED.`,
                  ``,
                  `━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
                  `  YOUR CERTIFICATE DETAILS`,
                  `━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
                  `Certificate Number : ${certNum}`,
                  `Business Name      : ${app.companyName}`,
                  `Plan               : ${planInfo?.name}`,
                  `Badge              : ${planInfo?.badge}`,
                  `Issue Date         : ${now.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}`,
                  `Valid Until        : ${expiryStr}`,
                  `━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
                  ``,
                  `You can verify your certificate anytime at:`,
                  `https://novacert.in (go to Verify Certificate and enter your cert number)`,
                  ``,
                  `Your business is now listed in the NovaCert Certified Directory.`,
                  ``,
                  `Please display your NovaCert badge proudly on your website, proposals, and social media.`,
                  ``,
                  `For any queries, reach us at info@novacert.in or +91 91150 19666.`,
                  ``,
                  `Warm regards,`,
                  `Team NovaCert`,
                  `Excellence Verified`,
                ].join("\n"),
              }),
            });
          } catch(emailErr) {
            console.warn("Approval email failed:", emailErr.message);
          }
        }
      }

      if (updates.status === "rejected") {
        // ── Auto email applicant on rejection ──
        try {
          await fetch(MAILER_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              to: app?.email,
              subject: `NovaCert Application Update — ${app?.companyName}`,
              message: [
                `Dear ${app?.fullName},`,
                ``,
                `Thank you for applying for NovaCert certification.`,
                ``,
                `After careful review, we regret to inform you that your application has not been approved at this time.`,
                ``,
                updates.adminComments ? `Reason: ${updates.adminComments}` : `Our team will contact you with more details.`,
                ``,
                `You are welcome to reapply after addressing the above points.`,
                ``,
                `For any queries, reach us at info@novacert.in or +91 91150 19666.`,
                ``,
                `Regards,`,
                `Team NovaCert`,
              ].join("\n"),
            }),
          });
        } catch(emailErr) {
          console.warn("Rejection email failed:", emailErr.message);
        }
      }

      await updateDoc(appRef, finalUpdates);

      // Update viewing if open
      if (viewing && viewing.id === id) {
        const updatedDoc = await getDoc(appRef);
        onView({ id: updatedDoc.id, ...updatedDoc.data() });
      }
    } catch (err) {
      console.error("Update error:", err);
      alert("Failed to update. Please try again.");
    }
    setActionLoading(false);
  };

  const filtered = applications.filter(a => {
    if (filter==="all") return true;
    if (filter==="expired") return a.status==="approved" && isExpired(a.expiryDate);
    return a.status === filter;
  });

  const counts = {
    all: applications.length,
    pending: applications.filter(a=>a.status==="pending").length,
    under_review: applications.filter(a=>a.status==="under_review").length,
    approved: applications.filter(a=>a.status==="approved").length,
    rejected: applications.filter(a=>a.status==="rejected").length,
    expired: applications.filter(a=>a.status==="approved" && isExpired(a.expiryDate)).length,
  };

  if (loading) {
    return (
      <section style={{maxWidth:1100,margin:"0 auto",padding:"48px 24px"}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:32,marginBottom:24}}>Admin Dashboard</h1>
        <div style={{textAlign:"center",padding:60,color:"#4B5563"}}><div style={{display:"flex",justifyContent:"center",marginBottom:12}}><Icons.Loader/></div>Loading applications...</div>
      </section>
    );
  }

  // ── DETAIL VIEW ──
  if (viewing) {
    const app = viewing;
    const planInfo = PLANS[app.plan];
    const expired = app.status==="approved" && isExpired(app.expiryDate);
    const statusInfo = expired ? STATUS_COLORS.expired : STATUS_COLORS[app.status];

    return (
      <section style={{maxWidth:800,margin:"0 auto",padding:"48px 24px"}}>
        <button onClick={()=>{onView(null);setComments("");}} style={{background:"none",border:"none",color:"#4B5563",cursor:"pointer",fontSize:14,fontFamily:"'DM Sans'",marginBottom:20}}>← Back to Dashboard</button>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12,marginBottom:24}}>
          <div>
            <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:28}}>{app.companyName}</h1>
            <p style={{color:"#4B5563",fontSize:14}}>{app.fullName} · {app.industry} · {app.city}</p>
          </div>
          <span className="status-badge" style={{background:statusInfo.bg,color:statusInfo.text}}>
            <span className="status-dot" style={{background:statusInfo.text}}/>{expired?"Expired":statusInfo.label}
          </span>
        </div>

        {/* Application Details */}
        <div className="nova-card" style={{padding:28,marginBottom:20}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:16,borderBottom:"1px solid #F5F5F4",paddingBottom:8}}>Application Details</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:"12px 24px",fontSize:14}}>
            {[["Plan",planInfo?.name],["Email",app.email],["Phone",app.phone],["WhatsApp",app.whatsapp||"—"],["Designation",app.designation||"—"],["Years in Business",app.yearsInBusiness||"—"],["Website",app.website||"—"],["GST",app.gstNumber||"—"],["PAN",app.panNumber||"—"],["Address",app.address||"—"],["Submitted",formatDate(app.submittedAt)]].map(([label,val])=>(
              <div key={label}><span style={{color:"#6B7280"}}>{label}:</span> {val}</div>
            ))}
          </div>
        </div>

        {/* Social Links */}
        <div className="nova-card" style={{padding:28,marginBottom:20}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:16,borderBottom:"1px solid #F5F5F4",paddingBottom:8}}>Online Presence</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))",gap:"8px 24px",fontSize:14}}>
            {[["Google Business",app.googleBusiness],["Facebook",app.facebook],["Instagram",app.instagram],["LinkedIn",app.linkedin],["Twitter/X",app.twitter],["YouTube",app.youtube]].map(([label,val])=>(
              <div key={label}><span style={{color:"#6B7280"}}>{label}:</span> {val ? <a href={val} target="_blank" rel="noopener noreferrer" style={{color:"#0D9488",wordBreak:"break-all"}}>{val.length>45?val.substring(0,45)+"...":val}</a> : "—"}</div>
            ))}
          </div>
        </div>

        {/* Uploaded Files */}
        <div className="nova-card" style={{padding:28,marginBottom:20}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:16,borderBottom:"1px solid #F5F5F4",paddingBottom:8}}>Uploaded Documents</h3>
          {["regDocs","officePhotos","portfolio"].map(cat => {
            const catFiles = app.files?.[cat] || [];
            const catLabel = cat==="regDocs"?"Registration Documents":cat==="officePhotos"?"Office Photos":"Portfolio / Work Samples";
            return (
              <div key={cat} style={{marginBottom:16}}>
                <span style={{fontSize:13,fontWeight:600,color:"#374151"}}>{catLabel} ({catFiles.length})</span>
                {catFiles.length === 0 ? <p style={{fontSize:13,color:"#6B7280",marginTop:4}}>Not uploaded</p> :
                  <div style={{marginTop:6}}>
                    {catFiles.map((f,i)=>(
                      <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:"#0D9488",padding:"3px 0",textDecoration:"none"}}>
                        <Icons.FileText/> {f.name}
                      </a>
                    ))}
                  </div>
                }
              </div>
            );
          })}
        </div>

        {/* Client References */}
        <div className="nova-card" style={{padding:28,marginBottom:20}}>
          <h3 style={{fontSize:16,fontWeight:700,marginBottom:16,borderBottom:"1px solid #F5F5F4",paddingBottom:8}}>Client References</h3>
          <div style={{fontSize:14}}>
            <p><strong>Ref 1:</strong> {app.clientRef1Name} · {app.clientRef1Phone} · {app.clientRef1Email||"—"}</p>
            {app.clientRef2Name && <p style={{marginTop:6}}><strong>Ref 2:</strong> {app.clientRef2Name} · {app.clientRef2Phone||"—"} · {app.clientRef2Email||"—"}</p>}
          </div>
          {app.aboutBusiness && (
            <div style={{marginTop:16}}>
              <span style={{color:"#6B7280",fontSize:13}}>About Business:</span>
              <p style={{fontSize:14,marginTop:4,lineHeight:1.6}}>{app.aboutBusiness}</p>
            </div>
          )}
        </div>

        {/* Certificate (if issued) */}
        {app.certNumber && (
          <div className="nova-card" style={{padding:28,marginBottom:20,border:`2px solid ${expired?"#F59E0B":"#10B981"}`}}>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:16,color:expired?"#92400E":"#059669"}}>Certificate {expired?"— EXPIRED":"— ACTIVE"}</h3>
            <div style={{fontSize:14,display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px 24px"}}>
              <div><span style={{color:"#6B7280"}}>Cert Number:</span> <strong>{app.certNumber}</strong></div>
              <div><span style={{color:"#6B7280"}}>Issued:</span> {formatDate(app.issuedAt)}</div>
              <div><span style={{color:"#6B7280"}}>Expires:</span> <strong style={{color:expired?"#DC2626":"#059669"}}>{formatDate(app.expiryDate)}</strong></div>
              <div><span style={{color:"#6B7280"}}>Badge:</span> {planInfo?.badge}</div>
            </div>
            {expired && (
              <div style={{marginTop:16,background:"#FEF3C7",padding:"10px 16px",borderRadius:8,fontSize:13,color:"#92400E"}}>
                ⚠ Renewal fee: ₹{planInfo?.renewal.toLocaleString("en-IN")} + GST
              </div>
            )}
          </div>
        )}

        {/* Admin Actions */}
        {app.status !== "approved" && (
          <div className="nova-card" style={{padding:28}}>
            <h3 style={{fontSize:16,fontWeight:700,marginBottom:16}}>Auditor Actions</h3>
            <div style={{marginBottom:16}}>
              <label className="nova-label">Comments / Rejection Reason</label>
              <textarea className="nova-input" rows={3} value={comments} onChange={e=>setComments(e.target.value)} placeholder="Add audit notes or rejection reason..."/>
            </div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              {app.status==="pending" && (
                <button className="nova-btn nova-btn-dark nova-btn-sm" disabled={actionLoading} onClick={()=>updateApplication(app.id,{status:"under_review"})}>
                  {actionLoading?<Icons.Loader/>:<Icons.Eye/>} Mark Under Review
                </button>
              )}
              <button className="nova-btn nova-btn-sm" style={{background:"#059669",color:"#fff"}} disabled={actionLoading} onClick={()=>updateApplication(app.id,{status:"approved",adminComments:comments})}>
                {actionLoading?<Icons.Loader/>:<Icons.Check/>} Issue Certificate
              </button>
              <button className="nova-btn nova-btn-danger nova-btn-sm" disabled={actionLoading} onClick={()=>{
                if(comments.trim()) updateApplication(app.id,{status:"rejected",adminComments:comments});
                else alert("Please add rejection comments before rejecting.");
              }}>
                {actionLoading?<Icons.Loader/>:<Icons.X/>} Reject
              </button>
            </div>
          </div>
        )}

        {app.adminComments && (
          <div style={{marginTop:16,background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:16,fontSize:14}}>
            <strong style={{color:"#92400E"}}>Admin Comments:</strong> {app.adminComments}
          </div>
        )}
      </section>
    );
  }

  // ── LIST VIEW ──
  return (
    <section style={{maxWidth:1100,margin:"0 auto",padding:"48px 24px"}}>
      <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:32,marginBottom:24}}>Admin Dashboard</h1>

      {/* Tab Switcher */}
      <div style={{display:"flex",gap:4,marginBottom:32,background:"#F5F5F4",borderRadius:10,padding:4,width:"fit-content"}}>
        {[["applications","📋 Applications"],["professionals","👤 Professionals"],["blog","✍️ Blog"]].map(([key,label])=>(
          <button key={key} onClick={()=>setAdminTab(key)} style={{padding:"10px 24px",borderRadius:8,border:"none",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",fontWeight:600,fontSize:14,background:adminTab===key?"#fff":"transparent",color:adminTab===key?"#0D9488":"#6B7280",boxShadow:adminTab===key?"0 1px 4px rgba(0,0,0,.1)":"none",transition:"all .2s"}}>
            {label}
          </button>
        ))}
      </div>

      {/* ── BLOG TAB ── */}
      {adminTab === "blog" && <AdminBlogPanel/>}

      {/* ── PROFESSIONALS TAB ── */}
      {adminTab === "professionals" && (
        <div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:22,fontWeight:700,color:"#0F172A",marginBottom:20}}>Professional Applications</h2>
          {professionals.length === 0 ? (
            <div style={{textAlign:"center",padding:48,color:"#6B7280"}}>
              <div style={{fontSize:40,marginBottom:12}}>👤</div>
              <p style={{fontSize:14}}>No professional applications yet.</p>
            </div>
          ) : professionals.map(pro => (
            <div key={pro.id} className="nova-card" style={{padding:20,marginBottom:16}}>
              <div style={{display:"flex",flexWrap:"wrap",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:12}}>
                <div>
                  <p style={{fontWeight:700,fontSize:16,color:"#0F172A",marginBottom:2}}>{pro.fullName||"—"}</p>
                  <p style={{fontSize:13,color:"#4B5563"}}>{pro.profession} · {pro.city}</p>
                  <p style={{fontSize:12,color:"#6B7280"}}>{pro.email} · {pro.phone}</p>
                </div>
                <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                  <span style={{fontSize:12,fontWeight:600,padding:"3px 10px",borderRadius:12,background:pro.status==="approved"?"#D1FAE5":pro.status==="rejected"?"#FEE2E2":"#FEF3C7",color:pro.status==="approved"?"#065F46":pro.status==="rejected"?"#B91C1C":"#92400E"}}>
                    {pro.status==="approved"?"✅ Certified":pro.status==="rejected"?"❌ Rejected":"⏳ Pending"}
                  </span>
                  <span style={{fontSize:12,fontWeight:600,padding:"3px 10px",borderRadius:12,background:"#F0FDFA",color:"#0D9488"}}>{pro.plan==="advanced"?"Advanced":"Verified"}</span>
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(180px,100%),1fr))",gap:"8px 20px",fontSize:13,color:"#374151",marginBottom:14,background:"#F8FAFC",borderRadius:8,padding:"12px 16px"}}>
                <div><span style={{color:"#6B7280",fontWeight:600}}>Education: </span>{pro.degree} — {pro.institution} ({pro.yearOfPassing})</div>
                <div><span style={{color:"#6B7280",fontWeight:600}}>Employer 1: </span>{pro.employer1} ({pro.employer1From}–{pro.employer1To})</div>
                <div><span style={{color:"#6B7280",fontWeight:600}}>Ref 1: </span>{pro.ref1Name} · {pro.ref1Phone}</div>
                <div><span style={{color:"#6B7280",fontWeight:600}}>Ref 2: </span>{pro.ref2Name} · {pro.ref2Phone}</div>
                {pro.cvUrl&&<div><a href={pro.cvUrl} target="_blank" rel="noreferrer" style={{color:"#0D9488",fontWeight:600}}>📄 Download CV</a></div>}
                {pro.certNumber&&<div><span style={{color:"#6B7280",fontWeight:600}}>Cert: </span><span style={{color:"#0D9488",fontWeight:700}}>{pro.certNumber}</span></div>}
              </div>
              {pro.status==="pending"&&(
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  <button className="nova-btn nova-btn-primary nova-btn-sm" onClick={()=>issueProfCert(pro)}>✅ Issue Certificate</button>
                  <button className="nova-btn nova-btn-danger nova-btn-sm" onClick={()=>updateProfessional(pro.id,{status:"rejected"})}>❌ Reject</button>
                </div>
              )}
              {pro.certNumber&&<p style={{fontSize:12,color:"#0D9488",fontWeight:700,marginTop:8}}>Certificate issued: {pro.certNumber}</p>}
            </div>
          ))}
        </div>
      )}

      {/* ── APPLICATIONS TAB ── */}
      {adminTab === "applications" && (<>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))",gap:12,marginBottom:32}}>
        {[
          {label:"Total",count:counts.all,color:"#1E293B",key:"all"},
          {label:"Pending",count:counts.pending,color:"#F59E0B",key:"pending"},
          {label:"Under Review",count:counts.under_review,color:"#3B82F6",key:"under_review"},
          {label:"Certified",count:counts.approved,color:"#10B981",key:"approved"},
          {label:"Rejected",count:counts.rejected,color:"#EF4444",key:"rejected"},
          {label:"Expired",count:counts.expired,color:"#6B7280",key:"expired"},
        ].map(s=>(
          <div key={s.key} onClick={()=>setFilter(s.key)} className="nova-card" style={{padding:"16px 20px",cursor:"pointer",borderColor:filter===s.key?s.color:undefined,borderWidth:filter===s.key?2:1}}>
            <div style={{fontSize:28,fontWeight:800,color:s.color}}>{s.count}</div>
            <div style={{fontSize:12,color:"#4B5563",fontWeight:600,textTransform:"uppercase",letterSpacing:.5}}>{s.label}</div>
          </div>
        ))}
      </div>

      {filtered.length===0 ? (
        <div style={{textAlign:"center",padding:64,color:"#6B7280"}}>
          <p style={{fontSize:16}}>No applications {filter!=="all"?`with status "${filter}"`:""} yet</p>
          <p style={{fontSize:13,marginTop:8}}>Applications submitted from the public form will appear here in real-time.</p>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {filtered.map(app => {
            const planInfo = PLANS[app.plan];
            const expired = app.status==="approved" && isExpired(app.expiryDate);
            const statusInfo = expired ? STATUS_COLORS.expired : STATUS_COLORS[app.status];
            return (
              <div key={app.id} className="nova-card" style={{padding:"20px 24px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}} onClick={()=>{onView(app);setComments(app.adminComments||"");}}>
                <div style={{display:"flex",alignItems:"center",gap:16,flex:1,minWidth:200}}>
                  <div style={{width:44,height:44,borderRadius:12,background:`${planInfo?.color}15`,color:planInfo?.color,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,flexShrink:0}}>
                    {app.plan==="credseal"?"CS":"PC"}
                  </div>
                  <div>
                    <h3 style={{fontSize:15,fontWeight:700}}>{app.companyName}</h3>
                    <p style={{fontSize:12,color:"#4B5563"}}>{app.fullName} · {app.industry} · {app.city}</p>
                  </div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:16}}>
                  {app.certNumber && <code style={{fontSize:11,color:"#4B5563",background:"#F5F5F4",padding:"2px 8px",borderRadius:4}}>{app.certNumber}</code>}
                  <span className="status-badge" style={{background:statusInfo.bg,color:statusInfo.text}}>
                    <span className="status-dot" style={{background:statusInfo.text}}/>{expired?"Expired":statusInfo.label}
                  </span>
                  <span style={{fontSize:12,color:"#6B7280"}} className="hide-mobile"><Icons.Clock/> {formatDate(app.submittedAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}
    </section>
  );
}


// ═══════════════════════════════════════════
// ─── ADMIN BLOG PANEL ────────────────────
// ═══════════════════════════════════════════
function AdminBlogPanel() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null=list, "new"=new post, {..}=edit post
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const BLANK = {
    title:"", excerpt:"", content:"", category:"",
    author:"NovaCert Team", coverImage:"", readTime:"",
    published: false,
  };
  const [form, setForm] = useState(BLANK);

  const CATEGORIES = ["Certification Tips","Business Trust","Freelancing","Industry News","CredSeal™","Case Study","Guides"];

  // Real-time listener
  useEffect(() => {
    const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const u = useCallback((field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(prev => ({...prev, [field]: val}));
  }, []);

  const openNew  = () => { setForm(BLANK); setEditing("new"); };
  const openEdit = (post) => { setForm({...BLANK, ...post}); setEditing(post); };
  const cancel   = () => { setEditing(null); setForm(BLANK); };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      alert("Title and content are required."); return;
    }
    setSaving(true);
    try {
      const now = new Date();
      if (editing === "new") {
        await addDoc(collection(db, "blogs"), {
          ...form,
          createdAt: now,
          publishedAt: form.published ? now : null,
        });
      } else {
        const ref2 = doc(db, "blogs", editing.id);
        const updates = { ...form };
        // Set publishedAt only when first publishing
        if (form.published && !editing.published) updates.publishedAt = now;
        await updateDoc(ref2, updates);
      }
      cancel();
    } catch(err) {
      alert("Save failed: " + err.message);
    }
    setSaving(false);
  };

  const deletePost = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    setDeleting(id);
    try {
      await updateDoc(doc(db, "blogs", id), { published: false });
      // Soft delete — just unpublish. To hard delete uncomment below:
      // await deleteDoc(doc(db, "blogs", id));
    } catch(err) { alert("Delete failed: " + err.message); }
    setDeleting(null);
  };

  const togglePublish = async (post) => {
    const updates = { published: !post.published };
    if (!post.published) updates.publishedAt = new Date();
    await updateDoc(doc(db, "blogs", post.id), updates);
  };

  // ── EDITOR VIEW ──
  if (editing !== null) {
    return (
      <div>
        <button onClick={cancel} style={{background:"none",border:"none",color:"#6B7280",cursor:"pointer",fontSize:14,fontFamily:"'DM Sans'",marginBottom:20}}>← Back to Posts</button>
        <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,marginBottom:28}}>{editing==="new"?"New Blog Post":"Edit Post"}</h2>

        <div className="nova-card" style={{padding:"36px 32px"}}>

          {/* Title */}
          <div style={{marginBottom:16}}>
            <label className="nova-label">Post Title <span style={{color:"#DC2626"}}>*</span></label>
            <input className="nova-input" value={form.title} onChange={u("title")} placeholder="Write a compelling title..." style={{fontSize:18,fontWeight:600}}/>
          </div>

          {/* Category + Author + Read Time */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:16,marginBottom:16}}>
            <div>
              <label className="nova-label">Category</label>
              <select className="nova-input" value={form.category} onChange={u("category")} style={{cursor:"pointer"}}>
                <option value="">Select category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="nova-label">Author</label>
              <input className="nova-input" value={form.author} onChange={u("author")} placeholder="NovaCert Team"/>
            </div>
            <div>
              <label className="nova-label">Read Time (mins)</label>
              <input className="nova-input" value={form.readTime} onChange={u("readTime")} placeholder="e.g. 5"/>
            </div>
          </div>

          {/* Cover Image URL */}
          <div style={{marginBottom:16}}>
            <label className="nova-label">Cover Image URL</label>
            <input className="nova-input" value={form.coverImage} onChange={u("coverImage")} placeholder="https://... (paste any image URL)"/>
            {form.coverImage && (
              <img src={form.coverImage} alt="cover preview" style={{marginTop:8,width:"100%",maxHeight:200,objectFit:"cover",borderRadius:8}} onError={e=>e.target.style.display="none"}/>
            )}
          </div>

          {/* Excerpt */}
          <div style={{marginBottom:16}}>
            <label className="nova-label">Excerpt <span style={{color:"#6B7280",fontWeight:400,textTransform:"none",fontSize:11}}>(short summary shown on blog listing)</span></label>
            <textarea className="nova-input" rows={2} value={form.excerpt} onChange={u("excerpt")} placeholder="One or two sentences summarising the post..."/>
          </div>

          {/* Content */}
          <div style={{marginBottom:20}}>
            <label className="nova-label">Content <span style={{color:"#DC2626"}}>*</span></label>
            <div style={{background:"#F0FDFA",border:"1px solid #99F6E4",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#0D9488",marginBottom:6}}>
              💡 Tip: Use <strong># Heading</strong> for big titles, <strong>## Subheading</strong> for sections, <strong>- </strong> for bullet points. Press Enter for new paragraphs.
            </div>
            <textarea
              className="nova-input"
              rows={20}
              value={form.content}
              onChange={u("content")}
              placeholder={`# Your Main Heading\n\nWrite your introduction here...\n\n## Section 1\n\nYour content...\n\n- Bullet point 1\n- Bullet point 2\n\n## Conclusion\n\nWrap it up here...`}
              style={{fontFamily:"'Courier New',monospace",fontSize:14,lineHeight:1.7}}
            />
            <p style={{fontSize:11,color:"#9CA3AF",marginTop:4}}>Word count: {form.content.trim().split(/\s+/).filter(Boolean).length}</p>
          </div>

          {/* Publish toggle */}
          <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginBottom:28,padding:"14px 16px",background:form.published?"#F0FDFA":"#F9FAFB",borderRadius:10,border:`2px solid ${form.published?"#0D9488":"#D6D3D1"}`}}>
            <input type="checkbox" checked={form.published} onChange={u("published")} style={{width:18,height:18,accentColor:"#0D9488"}}/>
            <div>
              <p style={{fontWeight:700,fontSize:14,color:form.published?"#0D9488":"#374151"}}>{form.published ? "✅ Published — visible on website" : "📝 Draft — not visible to public"}</p>
              <p style={{fontSize:12,color:"#6B7280",marginTop:2}}>You can save as draft and publish later</p>
            </div>
          </label>

          {/* Actions */}
          <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
            <button className="nova-btn nova-btn-primary" disabled={saving} onClick={save} style={{minWidth:160,justifyContent:"center"}}>
              {saving ? <><Icons.Loader/> Saving...</> : form.published ? "💾 Save & Publish" : "💾 Save as Draft"}
            </button>
            <button className="nova-btn nova-btn-secondary" onClick={cancel}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:26,marginBottom:4}}>Blog Posts</h2>
          <p style={{fontSize:13,color:"#6B7280"}}>{posts.filter(p=>p.published).length} published · {posts.filter(p=>!p.published).length} drafts</p>
        </div>
        <button className="nova-btn nova-btn-primary" onClick={openNew}>+ New Post</button>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:60}}><Icons.Loader/></div>
      ) : posts.length === 0 ? (
        <div className="nova-card" style={{padding:64,textAlign:"center"}}>
          <div style={{fontSize:48,marginBottom:16}}>✍️</div>
          <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:22,marginBottom:8}}>No posts yet</h3>
          <p style={{color:"#6B7280",fontSize:14,marginBottom:24}}>Write your first blog post to boost your SEO and share your expertise.</p>
          <button className="nova-btn nova-btn-primary" onClick={openNew}>Write First Post</button>
        </div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {posts.map(post => (
            <div key={post.id} className="nova-card" style={{padding:"20px 24px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
              {/* Cover thumb */}
              <div style={{width:64,height:48,borderRadius:8,background:"#F5F5F4",flexShrink:0,overflow:"hidden"}}>
                {post.coverImage
                  ? <img src={post.coverImage} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>
                  : <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>📝</div>
                }
              </div>

              {/* Info */}
              <div style={{flex:1,minWidth:180}}>
                <h3 style={{fontSize:15,fontWeight:700,marginBottom:4,color:"#0F172A"}}>{post.title}</h3>
                <div style={{display:"flex",gap:12,fontSize:12,color:"#6B7280",flexWrap:"wrap"}}>
                  {post.category && <span style={{background:"#F0FDFA",color:"#0D9488",padding:"2px 8px",borderRadius:10,fontWeight:600}}>{post.category}</span>}
                  <span>✍️ {post.author || "NovaCert Team"}</span>
                  <span>📅 {formatDate(post.publishedAt || post.createdAt)}</span>
                  <span>⏱ {post.readTime || "?"} min</span>
                  <span style={{color:"#9CA3AF"}}>{post.content?.trim().split(/\s+/).length || 0} words</span>
                </div>
              </div>

              {/* Status + Actions */}
              <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                <span style={{fontSize:12,fontWeight:600,padding:"4px 10px",borderRadius:12,background:post.published?"#D1FAE5":"#FEF3C7",color:post.published?"#065F46":"#92400E"}}>
                  {post.published ? "● Live" : "○ Draft"}
                </span>
                <button onClick={()=>togglePublish(post)} style={{padding:"6px 12px",fontSize:12,fontWeight:600,border:"1px solid #D6D3D1",borderRadius:6,background:"#fff",cursor:"pointer",fontFamily:"'DM Sans',sans-serif",color:"#374151"}}>
                  {post.published ? "Unpublish" : "Publish"}
                </button>
                <button onClick={()=>openEdit(post)} style={{padding:"6px 12px",fontSize:12,fontWeight:600,border:"1px solid #0D9488",borderRadius:6,background:"#fff",color:"#0D9488",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                  Edit
                </button>
                <button onClick={()=>deletePost(post.id)} disabled={deleting===post.id} style={{padding:"6px 10px",fontSize:12,border:"1px solid #FCA5A5",borderRadius:6,background:"#fff",color:"#DC2626",cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>
                  {deleting===post.id ? "..." : "🗑"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════
// ─── CERTIFIED DIRECTORY PAGE ────────────
// ═══════════════════════════════════════════
function DirectoryPage({ onNavigate }) {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterIndustry, setFilterIndustry] = useState("all");

  useEffect(() => {
    let unsub = null;
    let didLoad = false;

    // Primary — realtime listener
    try {
      const q = query(
        collection(db, "applications"),
        where("status", "==", "approved"),
        orderBy("issuedAt", "desc")
      );
      unsub = onSnapshot(q, (snap) => {
        didLoad = true;
        setBusinesses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      }, async (err) => {
        // Fallback — one-time getDocs if listener fails
        console.warn("onSnapshot failed, falling back to getDocs:", err);
        try {
          const snap = await getDocs(query(
            collection(db, "applications"),
            where("status", "==", "approved"),
            orderBy("issuedAt", "desc")
          ));
          setBusinesses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch(e) {
          console.error("getDocs fallback also failed:", e);
        }
        setLoading(false);
      });
    } catch(e) {
      setLoading(false);
    }

    // Safety timeout — if nothing loads in 8s, stop spinning
    const timeout = setTimeout(() => {
      if(!didLoad) setLoading(false);
    }, 8000);

    return () => { unsub?.(); clearTimeout(timeout); };
  }, []);

  // Filter out expired ones for display and apply search/filters
  const active = businesses.filter(b => !isExpired(b.expiryDate));

  const industries = ["all", ...Array.from(new Set(active.map(b => b.industry).filter(Boolean))).sort()];

  const filtered = active.filter(b => {
    const matchSearch = !search ||
      b.companyName?.toLowerCase().includes(search.toLowerCase()) ||
      b.city?.toLowerCase().includes(search.toLowerCase()) ||
      b.industry?.toLowerCase().includes(search.toLowerCase());
    const matchPlan   = filterPlan === "all" || b.plan === filterPlan;
    const matchInd    = filterIndustry === "all" || b.industry === filterIndustry;
    return matchSearch && matchPlan && matchInd;
  });

  const credsealCount     = active.filter(b => b.plan === "credseal").length;
  const professionalCount = active.filter(b => b.plan === "professional").length;

  return (
    <section style={{maxWidth:1100,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>

      {/* Header */}
      <div style={{textAlign:"center",marginBottom:48}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"#F0FDFA",border:"1px solid #99F6E4",padding:"6px 16px",borderRadius:20,fontSize:13,color:"#0D9488",marginBottom:16,fontWeight:600}}>
          ✅ Verified Businesses
        </div>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,44px)",marginBottom:12}}>NovaCert Certified Directory</h1>
        <p style={{color:"#4B5563",fontSize:16,maxWidth:540,margin:"0 auto"}}>Every business listed here has been thoroughly audited and verified by NovaCert.</p>

        {/* Stats */}
        <div style={{display:"inline-flex",gap:32,marginTop:24,padding:"16px 32px",background:"#fff",borderRadius:14,border:"1px solid #E7E5E4",boxShadow:"0 2px 12px rgba(0,0,0,.06)"}}>
          <div style={{textAlign:"center"}}>
            <p style={{fontSize:28,fontWeight:800,color:"#0D9488"}}>{active.length}</p>
            <p style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Total Certified</p>
          </div>
          <div style={{width:1,background:"#E7E5E4"}}/>
          <div style={{textAlign:"center"}}>
            <p style={{fontSize:28,fontWeight:800,color:"#7C3AED"}}>{credsealCount}</p>
            <p style={{fontSize:12,color:"#6B7280",fontWeight:600}}>CredSeal™</p>
          </div>
          <div style={{width:1,background:"#E7E5E4"}}/>
          <div style={{textAlign:"center"}}>
            <p style={{fontSize:28,fontWeight:800,color:"#0D9488"}}>{professionalCount}</p>
            <p style={{fontSize:12,color:"#6B7280",fontWeight:600}}>Professional</p>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div style={{display:"flex",gap:12,marginBottom:32,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{flex:"1 1 200px",minWidth:0,position:"relative"}}>
          <span style={{position:"absolute",left:14,top:"50%",transform:"translateY(-50%)",color:"#9CA3AF",pointerEvents:"none"}}><Icons.Search/></span>
          <input
            className="nova-input"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            placeholder="Search by name, city, industry..."
            style={{paddingLeft:44,width:"100%"}}
          />
        </div>
        <select className="nova-input nova-mobile-full" value={filterPlan} onChange={e=>setFilterPlan(e.target.value)} style={{width:"auto",cursor:"pointer",minWidth:140,flex:"1 1 140px"}}>
          <option value="all">All Plans</option>
          <option value="professional">Professional</option>
          <option value="credseal">CredSeal™</option>
        </select>
        <select className="nova-input nova-mobile-full" value={filterIndustry} onChange={e=>setFilterIndustry(e.target.value)} style={{width:"auto",cursor:"pointer",minWidth:140,flex:"1 1 140px"}}>
          <option value="all">All Industries</option>
          {industries.filter(i=>i!=="all").map(i=><option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      {/* Results count */}
      {!loading && (
        <p style={{fontSize:13,color:"#6B7280",marginBottom:20}}>
          Showing <strong>{filtered.length}</strong> certified {filtered.length===1?"business":"businesses"}
          {search ? ` matching "${search}"` : ""}
        </p>
      )}

      {/* Grid */}
      {loading ? (
        <div style={{textAlign:"center",padding:80,color:"#6B7280"}}>
          <Icons.Loader/>
          <p style={{marginTop:12,fontSize:14}}>Loading directory...</p>
          <p style={{marginTop:6,fontSize:12,color:"#9CA3AF"}}>This may take a few seconds on first load</p>
        </div>
      ) : filtered.length === 0 && businesses.length === 0 ? (
        <div style={{textAlign:"center",padding:80}}>
          <div style={{fontSize:48,marginBottom:16}}>🏢</div>
          <h3 style={{fontSize:20,fontWeight:700,marginBottom:8}}>Directory is loading</h3>
          <p style={{color:"#6B7280",marginBottom:16}}>Please refresh the page if this takes more than 10 seconds.</p>
          <button className="nova-btn nova-btn-primary" onClick={()=>window.location.reload()}>Refresh Page</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:80}}>
          <div style={{fontSize:48,marginBottom:16}}>🔍</div>
          <h3 style={{fontSize:20,fontWeight:700,marginBottom:8}}>No results found</h3>
          <p style={{color:"#6B7280"}}>Try a different search or filter.</p>
        </div>
      ) : (
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(min(280px,100%),1fr))",gap:20}}>
          {filtered.map(biz => {
            const planInfo = PLANS[biz.plan];
            const isCredSeal = biz.plan === "credseal";
            return (
              <div key={biz.id} className="nova-card" style={{padding:24,position:"relative",border:isCredSeal?"2px solid #7C3AED20":undefined}}>

                {/* CredSeal badge */}
                {isCredSeal && (
                  <div style={{position:"absolute",top:16,right:16,background:"linear-gradient(135deg,#7C3AED,#6D28D9)",color:"#fff",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,letterSpacing:.5,textTransform:"uppercase"}}>
                    CredSeal™
                  </div>
                )}

                {/* Business initial avatar */}
                <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:16}}>
                  <div style={{width:52,height:52,borderRadius:14,background:`linear-gradient(135deg,${planInfo?.color}20,${planInfo?.color}40)`,color:planInfo?.color,display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:18,flexShrink:0,fontFamily:"'Playfair Display',serif"}}>
                    {biz.companyName?.[0]?.toUpperCase() || "B"}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <h3 style={{fontSize:16,fontWeight:700,color:"#0F172A",marginBottom:2,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{biz.companyName}</h3>
                    <p style={{fontSize:12,color:"#6B7280"}}>{biz.city}{biz.state ? `, ${biz.state}` : ""}</p>
                  </div>
                </div>

                {/* Details */}
                <div style={{display:"flex",flexDirection:"column",gap:6,fontSize:13,marginBottom:16}}>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{color:"#6B7280"}}>Industry</span>
                    <span style={{fontWeight:600,color:"#374151"}}>{biz.industry || "—"}</span>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{color:"#6B7280"}}>Cert Number</span>
                    <code style={{fontSize:11,background:"#F5F5F4",padding:"2px 6px",borderRadius:4,color:"#374151"}}>{biz.certNumber}</code>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{color:"#6B7280"}}>Valid Until</span>
                    <span style={{fontWeight:600,color:"#059669"}}>{formatDate(biz.expiryDate)}</span>
                  </div>
                </div>

                {/* Badge */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",paddingTop:12,borderTop:"1px solid #F5F5F4"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:8,height:8,borderRadius:"50%",background:"#10B981"}}/>
                    <span style={{fontSize:12,fontWeight:600,color:"#059669"}}>Active Certificate</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:4,background:`${planInfo?.color}15`,padding:"4px 10px",borderRadius:20}}>
                    <Icons.Shield/>
                    <span style={{fontSize:11,fontWeight:700,color:planInfo?.color}}>{planInfo?.badge}</span>
                  </div>
                </div>

                {/* Website link if available */}
                {biz.website && (
                  <a href={biz.website} target="_blank" rel="noopener noreferrer" style={{display:"block",marginTop:10,fontSize:12,color:"#0D9488",textDecoration:"none",textAlign:"center",padding:"6px",background:"#F0FDFA",borderRadius:6}}>
                    🌐 Visit Website
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* CTA */}
      {!loading && active.length > 0 && (
        <div style={{textAlign:"center",marginTop:60,padding:"40px 24px",background:"linear-gradient(135deg,#0F172A,#1E293B)",borderRadius:16,color:"#fff"}}>
          <h3 style={{fontFamily:"'Playfair Display',serif",fontSize:24,marginBottom:8}}>Want to be listed here?</h3>
          <p style={{color:"#94A3B8",fontSize:14,marginBottom:24}}>Apply for NovaCert certification and get your business verified.</p>
          <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("certifications")}>Get Certified →</button>
        </div>
      )}
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// TERMS OF USE PAGE — NovaCert
// ═══════════════════════════════════════════════════════════════
function TermsPage({ onNavigate }) {
  const S = { heading: { fontFamily:"'Playfair Display',serif", fontWeight:700, color:"#0F172A", marginBottom:10 },
    body: { fontSize:14, color:"#374151", lineHeight:1.8, marginBottom:12 },
    list: { fontSize:14, color:"#374151", lineHeight:1.8, marginLeft:20, marginBottom:8 } };

  const Section = ({ title, children }) => (
    <div style={{marginBottom:36}}>
      <h2 style={{...S.heading, fontSize:18, paddingBottom:8, borderBottom:"2px solid #F0FDFA", marginBottom:16}}>{title}</h2>
      {children}
    </div>
  );

  return (
    <>
      {/* Hero */}
      <section style={{background:"linear-gradient(135deg,#0F172A,#1E3A5F)",padding:"64px 24px 48px",textAlign:"center"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <p style={{fontSize:11,fontWeight:700,color:"#0D9488",textTransform:"uppercase",letterSpacing:3,marginBottom:12}}>Legal</p>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,4vw,44px)",fontWeight:700,color:"#fff",marginBottom:12}}>Website Terms of Use</h1>
          <p style={{color:"rgba(255,255,255,.6)",fontSize:14}}>Applicable to novacert.in and credmark.org &nbsp;·&nbsp; Last updated: 29 March 2026</p>
        </div>
      </section>

      {/* Content */}
      <section style={{maxWidth:820,margin:"0 auto",padding:"56px 24px"}}>

        <div style={{background:"#F0FDFA",border:"1px solid #99F6E4",borderRadius:8,padding:"16px 20px",marginBottom:40,fontSize:14,color:"#134E4A"}}>
          These Terms of Use govern your access to and use of <strong>novacert.in</strong> and <strong>credmark.org</strong>, operated by NovaCert. By using these websites you agree to these Terms.
        </div>

        <Section title="1. Intellectual Property Rights">
          <p style={S.body}>All content on these websites — including text, graphics, logos, images, certification standards documentation, scoring frameworks, evaluation criteria, and software — is the property of NovaCert and is protected under the Copyright Act, 1957 and applicable intellectual property laws.</p>
          <p style={S.body}>The <strong>CredMark™</strong> name, logo, certification badge, and associated branding are trademarks of NovaCert (trademark applications filed under the Trade Marks Act, 1999). The <strong>NovaCert</strong> name, logo, and associated branding are trademarks of NovaCert. Unauthorised use is strictly prohibited.</p>
          <p style={S.body}>No content from these websites may be reproduced, distributed, modified, displayed, or transmitted in any form without the prior written permission of NovaCert.</p>
        </Section>

        <Section title="2. Restrictions on Use">
          <p style={S.body}>You shall not:</p>
          {[
            "Copy, reproduce, or distribute any content from the websites",
            "Reverse engineer or attempt to derive the CredMark™ evaluation methodology, scoring algorithms, or proprietary processes",
            "Use any automated tool, scraper, bot, or data mining technique to extract data",
            "Frame, mirror, or deep-link to any portion of the websites without written permission",
            "Use any content to train artificial intelligence or machine learning models",
            "Use the CredMark™ badge or NovaCert logo without a valid certification and written licence",
            "Create derivative works based on the CredMark™ standard or certification criteria",
            "Use the websites for any unlawful purpose or in violation of applicable law",
          ].map((item,i) => <p key={i} style={S.list}>• &nbsp;{item}</p>)}
        </Section>

        <Section title="3. Certification Badge Usage">
          <p style={S.body}>The CredMark™ certification badge and NovaCert Verified badge may only be used by businesses holding a valid, current certification issued by NovaCert. Unauthorised use constitutes trademark infringement and will be prosecuted to the fullest extent under Indian law.</p>
          <p style={S.body}>Upon expiry, suspension, or revocation of certification, all badge usage must cease immediately.</p>
        </Section>

        <Section title="4. Certificate Verification">
          <p style={S.body}>The online certificate verification service is provided for informational purposes only. Verification results reflect the certification status at the time of query. NovaCert makes no guarantee regarding the future performance of certified businesses. Users should exercise their own judgment and due diligence in addition to relying on certification status.</p>
        </Section>

        <Section title="5. Disclaimer of Warranties">
          <p style={S.body}>The websites and all content are provided "as is" and "as available" without any warranties, express or implied. NovaCert does not warrant that the websites will be uninterrupted, error-free, or free of viruses or harmful components. Certification represents compliance with the CredMark™ standard at the time of assessment only and does not guarantee future performance.</p>
        </Section>

        <Section title="6. Limitation of Liability">
          <p style={S.body}>To the maximum extent permitted by law, NovaCert shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the websites. NovaCert shall not be liable for any business decisions made in reliance on certification status displayed on the websites. NovaCert's total liability shall not exceed the amount paid by you to NovaCert (if any) in the preceding 12 months.</p>
        </Section>

        <Section title="7. Privacy and Data Protection">
          <p style={S.body}>NovaCert collects and processes personal data in accordance with applicable Indian data protection laws, including the Digital Personal Data Protection Act, 2023. By submitting information through these websites, you consent to the collection, storage, and processing of your personal data for certification purposes.</p>
          <p style={S.body}>NovaCert implements reasonable security measures to protect personal data against unauthorised access, as required under Section 43A of the Information Technology Act, 2000. For privacy-related enquiries: <a href="mailto:info@novacert.in" style={{color:"#0D9488"}}>info@novacert.in</a></p>
        </Section>

        <Section title="8. Grievance Officer">
          <p style={S.body}>In compliance with the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021:</p>
          <div style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:6,padding:"16px 20px",fontSize:14,color:"#374151"}}>
            <p style={{marginBottom:6}}><strong>Grievance Officer:</strong> Proprietor, NovaCert</p>
            <p style={{marginBottom:6}}><strong>Email:</strong> <a href="mailto:info@novacert.in" style={{color:"#0D9488"}}>info@novacert.in</a></p>
            <p style={{marginBottom:6}}><strong>Phone:</strong> +91 91150 19666</p>
            <p style={{marginBottom:6}}><strong>Address:</strong> 538K/449, Triveni Nagar Colony II, Sitapur Road, Lucknow, Uttar Pradesh – 226020</p>
            <p style={{marginBottom:0,color:"#64748B",fontSize:13}}>Complaints acknowledged within 24 hours and resolved within 15 days.</p>
          </div>
        </Section>

        <Section title="9. Governing Law and Jurisdiction">
          <p style={S.body}>These Terms shall be governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Lucknow, Uttar Pradesh.</p>
        </Section>

        <Section title="10. Changes to These Terms">
          <p style={S.body}>NovaCert reserves the right to modify these Terms at any time. Changes will be posted on the websites with the updated "Last updated" date. Continued use after changes constitutes acceptance.</p>
        </Section>

        <Section title="11. Contact">
          <p style={S.body}>For any questions about these Terms: <a href="mailto:info@novacert.in" style={{color:"#0D9488"}}>info@novacert.in</a> &nbsp;·&nbsp; +91 91150 19666 &nbsp;·&nbsp; 538K/449, Triveni Nagar Colony II, Sitapur Road, Lucknow, Uttar Pradesh – 226020</p>
        </Section>

        <div style={{marginTop:48,padding:"20px 24px",background:"#F8FAFC",borderRadius:6,border:"1px solid #E2E8F0",textAlign:"center"}}>
          <p style={{fontSize:13,color:"#64748B"}}>© {new Date().getFullYear()} NovaCert. All rights reserved. &nbsp;·&nbsp; novacert.in &nbsp;·&nbsp; credmark.org</p>
        </div>

      </section>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// PROFESSIONAL LANDING PAGE
// ═══════════════════════════════════════════════════════════════
function ProfessionalPage({ onNavigate }) {
  return (
    <>
      {/* Hero */}
      <section style={{background:"linear-gradient(135deg,#0F172A 0%,#1E293B 100%)",padding:"clamp(56px,10vw,100px) 16px clamp(48px,8vw,80px)",textAlign:"center",color:"#fff",position:"relative",overflow:"hidden"}}>
        <div style={{position:"absolute",top:-80,right:-80,width:400,height:400,borderRadius:"50%",background:"radial-gradient(circle,rgba(13,148,136,.12) 0%,transparent 70%)"}}/>
        <div style={{maxWidth:760,margin:"0 auto",position:"relative"}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:8,background:"rgba(13,148,136,.15)",border:"1px solid rgba(13,148,136,.3)",padding:"5px 16px",borderRadius:20,fontSize:12,color:"#34D399",marginBottom:20,fontWeight:600}}>
            🆕 New — NovaCert Professional
          </div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(28px,5vw,52px)",fontWeight:700,marginBottom:16,lineHeight:1.1}}>
            Your CV Verified.<br/>
            <span style={{color:"#0D9488"}}>Your Credentials Proven.</span>
          </h1>
          <p style={{fontSize:16,color:"#CBD5E1",maxWidth:580,margin:"0 auto 36px",lineHeight:1.8}}>
            Stop losing jobs to candidates with inflated CVs. NovaCert Professional independently verifies your identity, education, employment history, and skills — giving recruiters the confidence to choose you.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("pro-apply")} style={{fontSize:15,padding:"14px 32px"}}>Apply for Verification →</button>
            <button className="nova-btn" onClick={()=>onNavigate("pro-verify")} style={{fontSize:15,padding:"14px 32px",background:"rgba(255,255,255,.1)",color:"#fff",border:"1px solid rgba(255,255,255,.2)"}}>Verify a Certificate</button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{background:"#F0FDFA",borderBottom:"1px solid #CCFBF1",padding:"32px 24px"}}>
        <div style={{maxWidth:900,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px,100%),1fr))",gap:24,textAlign:"center"}}>
          {[["For Professionals","Get your CV independently verified"],["For Recruiters","Verify any candidate in seconds"],["For HR Teams","Stop wasting time on fake CVs"]].map(([t,s])=>(
            <div key={t}>
              <p style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:4}}>{t}</p>
              <p style={{fontSize:13,color:"#4B5563"}}>{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{maxWidth:1000,margin:"0 auto",padding:"clamp(40px,7vw,80px) 16px"}}>
        <div style={{textAlign:"center",marginBottom:48}}>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(24px,3.5vw,36px)",fontWeight:700,color:"#0F172A",marginBottom:10}}>How It Works</h2>
          <p style={{color:"#4B5563",fontSize:15,maxWidth:480,margin:"0 auto"}}>A rigorous 20–30 day independent verification process</p>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(220px,100%),1fr))",gap:20}}>
          {[
            {n:"01",icon:"📋",title:"Apply Online",desc:"Fill the application form, pay the fee, and upload your CV and supporting documents."},
            {n:"02",icon:"🔍",title:"Independent Audit",desc:"Our team independently contacts your employers, educational institutions, and references."},
            {n:"03",icon:"✅",title:"Certificate Issued",desc:"If verified, you receive a NovaCert Professional certificate with a unique verifiable number."},
            {n:"04",icon:"🏢",title:"Recruiters Verify",desc:"Share your certificate number with any recruiter who can verify your credentials instantly."},
          ].map(s=>(
            <div key={s.n} className="nova-card" style={{padding:"24px 20px",textAlign:"center"}}>
              <div style={{width:36,height:36,borderRadius:"50%",background:"#0F172A",color:"#0D9488",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px"}}>{s.n}</div>
              <div style={{fontSize:28,marginBottom:10}}>{s.icon}</div>
              <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:8}}>{s.title}</h3>
              <p style={{fontSize:13,color:"#4B5563",lineHeight:1.6}}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What gets verified */}
      <section style={{background:"#F8FAFC",borderTop:"1px solid #E7E5E4",borderBottom:"1px solid #E7E5E4",padding:"clamp(40px,7vw,80px) 16px"}}>
        <div style={{maxWidth:900,margin:"0 auto"}}>
          <div style={{textAlign:"center",marginBottom:40}}>
            <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(22px,3vw,34px)",fontWeight:700,color:"#0F172A",marginBottom:10}}>What Gets Independently Verified</h2>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(260px,100%),1fr))",gap:16}}>
            {[
              {icon:"🪪",title:"Identity",desc:"Aadhaar / PAN verified. Confirms you are who you say you are."},
              {icon:"🎓",title:"Education",desc:"Degrees, institutions, and years of study independently confirmed."},
              {icon:"💼",title:"Employment History",desc:"Previous employers contacted and tenure verified directly."},
              {icon:"🛠",title:"Skills",desc:"Skills claimed are independently assessed or referenced."},
              {icon:"📞",title:"References",desc:"Reference contacts are called independently — not just accepted."},
              {icon:"🚫",title:"No Active Complaints",desc:"Consumer complaint databases checked. Clean record confirmed."},
            ].map(v=>(
              <div key={v.title} className="nova-card" style={{padding:"20px",display:"flex",gap:14,alignItems:"flex-start"}}>
                <span style={{fontSize:24,flexShrink:0}}>{v.icon}</span>
                <div>
                  <p style={{fontWeight:700,fontSize:14,color:"#0F172A",marginBottom:4}}>{v.title}</p>
                  <p style={{fontSize:12,color:"#4B5563",lineHeight:1.6}}>{v.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy note */}
      <section style={{maxWidth:800,margin:"0 auto",padding:"clamp(32px,6vw,64px) 16px",textAlign:"center"}}>
        <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:12,padding:"28px 32px",marginBottom:32}}>
          <div style={{fontSize:32,marginBottom:12}}>🔒</div>
          <h3 style={{fontWeight:700,fontSize:18,color:"#0F172A",marginBottom:8}}>Your Privacy is Protected</h3>
          <p style={{fontSize:14,color:"#4B5563",lineHeight:1.7,maxWidth:540,margin:"0 auto"}}>Your professional profile is never publicly listed or searchable. Only someone who has your certificate number can verify your details. You control who sees your credentials.</p>
        </div>

        {/* Pricing */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(240px,100%),1fr))",gap:16,marginBottom:32}}>
          {[
            {title:"Professional Verified",price:"₹1,999",gst:"+ GST",renewal:"₹999 / year renewal",color:"#0D9488",features:["Identity verified","Education verified","2 employer contacts","2 references called","Certificate valid 1 year"]},
            {title:"Professional Advanced",price:"₹2,999",gst:"+ GST",renewal:"₹1,499 / year renewal",color:"#7C3AED",features:["Everything in Standard","Full employment history","Skills assessment","5 references called","Priority processing"]},
          ].map(p=>(
            <div key={p.title} className="nova-card" style={{padding:"24px",border:`2px solid ${p.color}20`,textAlign:"left"}}>
              <h3 style={{fontWeight:700,fontSize:16,color:"#0F172A",marginBottom:4}}>{p.title}</h3>
              <div style={{fontSize:32,fontWeight:800,color:p.color,marginBottom:2}}>{p.price}</div>
              <p style={{fontSize:12,color:"#6B7280",marginBottom:16}}>{p.gst} &nbsp;·&nbsp; {p.renewal}</p>
              {p.features.map(f=>(
                <div key={f} style={{display:"flex",gap:8,alignItems:"center",marginBottom:8,fontSize:13,color:"#374151"}}>
                  <span style={{color:p.color,fontSize:14,flexShrink:0}}>✓</span>{f}
                </div>
              ))}
              <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("pro-apply")} style={{width:"100%",justifyContent:"center",marginTop:16,background:p.color}}>Apply Now →</button>
            </div>
          ))}
        </div>

        <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
          <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("pro-apply")} style={{fontSize:15,padding:"14px 32px"}}>Apply for Verification</button>
          <button className="nova-btn" onClick={()=>onNavigate("pro-verify")} style={{fontSize:15,padding:"14px 32px",border:"2px solid #0D9488",color:"#0D9488",background:"transparent"}}>Verify a Certificate</button>
        </div>
      </section>

      {/* For Recruiters CTA */}
      <section style={{background:"#0F172A",padding:"48px 24px",textAlign:"center"}}>
        <div style={{maxWidth:600,margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(22px,3vw,32px)",fontWeight:700,color:"#fff",marginBottom:12}}>Are You a Recruiter or HR Manager?</h2>
          <p style={{color:"#CBD5E1",fontSize:14,marginBottom:28,lineHeight:1.7}}>Verify any candidate's NovaCert Professional certificate in seconds. No login, no subscription. Just enter their certificate number.</p>
          <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("pro-verify")} style={{fontSize:15,padding:"14px 32px"}}>Verify a Candidate Now →</button>
        </div>
      </section>
    </>
  );
}


// ═══════════════════════════════════════════════════════════════
// PROFESSIONAL APPLICATION FORM
// ═══════════════════════════════════════════════════════════════
function ProfessionalForm({ onSubmit, onBack }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cvFile, setCvFile] = useState(null);
  const [form, setForm] = useState({
    fullName:"", email:"", phone:"", city:"", state:"",
    profession:"", experienceYears:"", currentEmployer:"",
    plan:"verified",
    // Education
    degree:"", institution:"", yearOfPassing:"",
    // Employment
    employer1:"", employer1From:"", employer1To:"", employer1Role:"",
    employer2:"", employer2From:"", employer2To:"", employer2Role:"",
    // References
    ref1Name:"", ref1Phone:"", ref1Relation:"",
    ref2Name:"", ref2Phone:"", ref2Relation:"",
    // Identity
    panNumber:"", aadhaarLast4:"",
    // Consent
    consent:false,
  });

  const u = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm(prev => ({...prev, [field]: val}));
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert("CV file must be under 5MB."); return; }
    setCvFile(file);
  };

  const handleSubmit = async () => {
    if (!form.consent) { alert("Please agree to the terms before submitting."); return; }
    if (!cvFile) { alert("Please upload your CV."); return; }
    setSubmitting(true);
    try {
      let cvUrl = "";
      if (cvFile) {
        setUploading(true);
        const storRef = ref(storage, `professionals/${Date.now()}_${cvFile.name.replace(/[^a-zA-Z0-9._-]/g,"_")}`);
        await uploadBytes(storRef, cvFile);
        cvUrl = await getDownloadURL(storRef);
        setUploading(false);
      }
      await addDoc(collection(db, "professionals"), {
        ...form,
        cvUrl,
        status: "pending",
        submittedAt: serverTimestamp(),
        certNumber: null,
        issuedAt: null,
        expiryDate: null,
      });
      onSubmit();
    } catch(err) {
      alert("Submission failed: " + err.message);
    }
    setSubmitting(false);
  };

  const inp = (label, field, type="text", placeholder="", required=false) => (
    <div style={{marginBottom:14}}>
      <label className="nova-label">{label}{required&&" *"}</label>
      <input className="nova-input" type={type} value={form[field]} onChange={u(field)} placeholder={placeholder}/>
    </div>
  );

  const steps = ["Personal Info","Education","Employment","References","Identity & CV","Review"];
  const progress = ((step-1)/5)*100;

  return (
    <section style={{maxWidth:720,margin:"0 auto",padding:"clamp(32px,6vw,60px) 16px"}}>
      <button onClick={onBack} style={{background:"none",border:"none",color:"#4B5563",cursor:"pointer",fontSize:14,marginBottom:24,display:"flex",alignItems:"center",gap:4,fontFamily:"'DM Sans',sans-serif"}}>← Back</button>

      <div style={{textAlign:"center",marginBottom:32}}>
        <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(22px,3vw,32px)",fontWeight:700,color:"#0F172A",marginBottom:8}}>Apply for Professional Verification</h1>
        <p style={{color:"#4B5563",fontSize:14}}>Step {step} of 6 — {steps[step-1]}</p>
      </div>

      {/* Progress bar */}
      <div style={{height:4,background:"#E7E5E4",borderRadius:2,marginBottom:32,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${progress}%`,background:"linear-gradient(90deg,#0D9488,#14B8A6)",borderRadius:2,transition:"width .3s"}}/>
      </div>

      {/* Plan selector — step 1 */}
      {step === 1 && (
        <div>
          {/* Plan choice */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:24}}>
            {[{id:"verified",label:"Professional Verified",price:"₹1,999 + GST",color:"#0D9488"},{id:"advanced",label:"Professional Advanced",price:"₹2,999 + GST",color:"#7C3AED"}].map(p=>(
              <div key={p.id} onClick={()=>setForm(prev=>({...prev,plan:p.id}))} style={{padding:"16px",borderRadius:10,border:`2px solid ${form.plan===p.id?p.color:"#E7E5E4"}`,cursor:"pointer",background:form.plan===p.id?`${p.color}08`:"#fff",transition:"all .2s"}}>
                <p style={{fontWeight:700,fontSize:14,color:"#0F172A",marginBottom:4}}>{p.label}</p>
                <p style={{fontSize:13,color:p.color,fontWeight:700}}>{p.price}</p>
              </div>
            ))}
          </div>
          {inp("Full Name","fullName","text","As per Aadhaar / PAN",true)}
          {inp("Email Address","email","email","your@email.com",true)}
          {inp("Phone Number","phone","tel","+91 98765 43210",true)}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px,100%),1fr))",gap:14}}>
            {inp("City","city","text","Lucknow")}
            {inp("State","state","text","Uttar Pradesh")}
          </div>
          {inp("Profession / Job Title","profession","text","e.g. Digital Marketing Manager",true)}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px,100%),1fr))",gap:14}}>
            {inp("Years of Experience","experienceYears","number","5")}
            {inp("Current / Last Employer","currentEmployer","text","Company Name")}
          </div>
        </div>
      )}

      {/* Education — step 2 */}
      {step === 2 && (
        <div>
          <div style={{background:"#F0FDFA",border:"1px solid #CCFBF1",borderRadius:8,padding:"14px 16px",marginBottom:24,fontSize:13,color:"#0F766E"}}>
            🎓 We will independently contact your institution to verify your qualification. Please provide accurate information.
          </div>
          {inp("Highest Degree / Qualification","degree","text","e.g. B.Tech, MBA, CA",true)}
          {inp("Institution / University Name","institution","text","e.g. University of Lucknow",true)}
          {inp("Year of Passing","yearOfPassing","number","2018",true)}
        </div>
      )}

      {/* Employment — step 3 */}
      {step === 3 && (
        <div>
          <div style={{background:"#F0FDFA",border:"1px solid #CCFBF1",borderRadius:8,padding:"14px 16px",marginBottom:24,fontSize:13,color:"#0F766E"}}>
            💼 We will independently contact these employers to verify your tenure and role. Please provide accurate details.
          </div>
          <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:12}}>Employer 1 (Most Recent)</h3>
          {inp("Company Name","employer1","text","",true)}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px,100%),1fr))",gap:14}}>
            {inp("From (Year)","employer1From","number","2020")}
            {inp("To (Year or Present)","employer1To","text","Present")}
          </div>
          {inp("Your Role / Designation","employer1Role","text","Senior Executive")}

          <div style={{borderTop:"1px solid #E7E5E4",margin:"24px 0"}}/>
          <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:12}}>Employer 2 (Previous)</h3>
          {inp("Company Name","employer2","text","")}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px,100%),1fr))",gap:14}}>
            {inp("From (Year)","employer2From","number","2017")}
            {inp("To (Year)","employer2To","number","2020")}
          </div>
          {inp("Your Role / Designation","employer2Role","text","Executive")}
        </div>
      )}

      {/* References — step 4 */}
      {step === 4 && (
        <div>
          <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,padding:"14px 16px",marginBottom:24,fontSize:13,color:"#92400E"}}>
            📞 Our auditors will independently call these references. Please ensure they are contactable and aware you have provided their details.
          </div>
          <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:12}}>Reference 1</h3>
          {inp("Full Name","ref1Name","text","",true)}
          {inp("Phone Number","ref1Phone","tel","+91 98765 43210",true)}
          {inp("Relationship","ref1Relation","text","e.g. Former Manager, Client, Professor")}

          <div style={{borderTop:"1px solid #E7E5E4",margin:"24px 0"}}/>
          <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:12}}>Reference 2</h3>
          {inp("Full Name","ref2Name","text","",true)}
          {inp("Phone Number","ref2Phone","tel","+91 98765 43210",true)}
          {inp("Relationship","ref2Relation","text","e.g. Former Colleague, Supervisor")}
        </div>
      )}

      {/* Identity & CV — step 5 */}
      {step === 5 && (
        <div>
          <div style={{background:"#F0FDFA",border:"1px solid #CCFBF1",borderRadius:8,padding:"14px 16px",marginBottom:24,fontSize:13,color:"#0F766E"}}>
            🔒 Your identity information is used for verification only and is never shared publicly.
          </div>
          {inp("PAN Card Number","panNumber","text","ABCDE1234F",true)}
          {inp("Last 4 digits of Aadhaar","aadhaarLast4","number","",true)}

          <div style={{borderTop:"1px solid #E7E5E4",margin:"24px 0"}}/>
          <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:12}}>Upload Your CV *</h3>
          <p style={{fontSize:13,color:"#4B5563",marginBottom:12}}>Upload your latest CV in PDF or Word format. Maximum 5MB.</p>

          <label style={{display:"block",border:"2px dashed #D1D5DB",borderRadius:10,padding:"32px 24px",textAlign:"center",cursor:"pointer",background:cvFile?"#F0FDFA":"#FAFAF9",transition:"all .2s"}}
            onMouseEnter={e=>e.currentTarget.style.borderColor="#0D9488"}
            onMouseLeave={e=>e.currentTarget.style.borderColor=cvFile?"#0D9488":"#D1D5DB"}>
            <input type="file" accept=".pdf,.doc,.docx" onChange={handleCVUpload} style={{display:"none"}}/>
            {cvFile ? (
              <div>
                <div style={{fontSize:28,marginBottom:8}}>✅</div>
                <p style={{fontWeight:700,fontSize:14,color:"#0F172A",marginBottom:4}}>{cvFile.name}</p>
                <p style={{fontSize:12,color:"#4B5563"}}>Click to replace</p>
              </div>
            ) : (
              <div>
                <div style={{fontSize:36,marginBottom:8}}>📄</div>
                <p style={{fontWeight:700,fontSize:14,color:"#0F172A",marginBottom:4}}>Click to upload CV</p>
                <p style={{fontSize:12,color:"#4B5563"}}>PDF, DOC, DOCX · Max 5MB</p>
              </div>
            )}
          </label>
        </div>
      )}

      {/* Review — step 6 */}
      {step === 6 && (
        <div>
          <div className="nova-card" style={{padding:"24px",marginBottom:20}}>
            <h3 style={{fontWeight:700,fontSize:15,color:"#0F172A",marginBottom:16}}>Application Summary</h3>
            {[
              ["Plan", form.plan === "verified" ? "Professional Verified — ₹1,999 + GST" : "Professional Advanced — ₹2,999 + GST"],
              ["Name", form.fullName],
              ["Email", form.email],
              ["Phone", form.phone],
              ["Profession", form.profession],
              ["Experience", `${form.experienceYears} years`],
              ["Education", `${form.degree} — ${form.institution} (${form.yearOfPassing})`],
              ["Employer 1", form.employer1],
              ["CV", cvFile ? cvFile.name : "Not uploaded"],
            ].map(([l,v])=>(
              <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid #F3F4F6",fontSize:13}}>
                <span style={{color:"#6B7280",fontWeight:600}}>{l}</span>
                <span style={{color:"#0F172A",fontWeight:500,textAlign:"right",maxWidth:"60%"}}>{v||"—"}</span>
              </div>
            ))}
          </div>

          <div style={{background:"#F0FDFA",border:"1px solid #0D9488",borderRadius:8,padding:"16px 18px",marginBottom:20,fontSize:13,color:"#0F172A",lineHeight:1.7}}>
            <strong>What happens next:</strong> Our team will contact you within 48 hours to confirm payment and begin the verification process. Verification takes 20–30 days. You will receive your certificate by email and can share the certificate number with any recruiter for instant verification.
          </div>

          <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",marginBottom:24}}>
            <input type="checkbox" checked={form.consent} onChange={u("consent")} style={{marginTop:2,accentColor:"#0D9488",width:16,height:16}}/>
            <span style={{fontSize:13,color:"#374151",lineHeight:1.6}}>I confirm that all information provided is accurate and truthful. I authorise NovaCert to independently contact my employers, educational institutions, and references for verification purposes. I agree to the NovaCert Terms of Use.</span>
          </label>
        </div>
      )}

      {/* Navigation buttons */}
      <div style={{display:"flex",justifyContent:"space-between",marginTop:32,gap:12}}>
        {step > 1 && (
          <button className="nova-btn" onClick={()=>setStep(s=>s-1)} style={{border:"2px solid #E7E5E4",color:"#374151",background:"#fff"}}>← Previous</button>
        )}
        <div style={{flex:1}}/>
        {step < 6 ? (
          <button className="nova-btn nova-btn-primary" onClick={()=>setStep(s=>s+1)}>Continue →</button>
        ) : (
          <button className="nova-btn nova-btn-primary" onClick={handleSubmit} disabled={submitting} style={{minWidth:160,justifyContent:"center"}}>
            {submitting ? (uploading?"Uploading CV...":"Submitting...") : "Submit Application 🚀"}
          </button>
        )}
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════════════════════════
// PROFESSIONAL SUCCESS PAGE
// ═══════════════════════════════════════════════════════════════
function ProSuccessPage({ onNavigate }) {
  return (
    <section style={{maxWidth:640,margin:"0 auto",padding:"clamp(32px,6vw,80px) 24px",textAlign:"center"}}>
      <div style={{fontSize:56,marginBottom:16}}>🎉</div>
      <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(24px,3.5vw,36px)",fontWeight:700,color:"#0F172A",marginBottom:12}}>Application Submitted!</h1>
      <p style={{fontSize:15,color:"#4B5563",lineHeight:1.8,marginBottom:32,maxWidth:480,margin:"0 auto 32px"}}>
        Thank you for applying for NovaCert Professional Verification. Our team will contact you within 48 hours to confirm payment and begin the verification process.
      </p>
      <div style={{background:"#F0FDFA",border:"1px solid #99F6E4",borderRadius:12,padding:"24px",marginBottom:32,textAlign:"left"}}>
        <h3 style={{fontWeight:700,fontSize:16,color:"#0F172A",marginBottom:16}}>What Happens Next</h3>
        {["Our team contacts you within 48 hours to confirm payment","Verification process begins — 20 to 30 days","Your employers, institutions and references are independently contacted","Certificate issued and emailed to you with a unique certificate number","Share your certificate number with any recruiter for instant verification"].map((s,i)=>(
          <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:12,fontSize:13,color:"#374151"}}>
            <span style={{background:"#0D9488",color:"#fff",borderRadius:"50%",width:20,height:20,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,flexShrink:0,marginTop:1}}>{i+1}</span>
            {s}
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
        <button className="nova-btn nova-btn-primary" onClick={()=>onNavigate("home")}>Back to Home</button>
        <button className="nova-btn" onClick={()=>onNavigate("pro-verify")} style={{border:"2px solid #0D9488",color:"#0D9488",background:"transparent"}}>Verify a Certificate</button>
      </div>
    </section>
  );
}


// ═══════════════════════════════════════════════════════════════
// PROFESSIONAL VERIFY PAGE
// ═══════════════════════════════════════════════════════════════
function ProVerifyPage({ result, setResult }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const search = async () => {
    const q = query.trim().toUpperCase();
    if (!q) return;
    setLoading(true);
    try {
      const snap = await getDocs(
        firestoreQuery(collection(db,"professionals"), where("certNumber","==",q))
      );
      setResult(snap.empty ? "not_found" : {id:snap.docs[0].id,...snap.docs[0].data()});
    } catch { setResult("error"); }
    setLoading(false);
  };

  const fmt = (ts) => {
    if(!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
  };

  const expired = (ts) => {
    if(!ts) return false;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d < new Date();
  };

  return (
    <>
      {/* Hero */}
      <section style={{background:"linear-gradient(135deg,#0F172A,#1E293B)",padding:"clamp(40px,8vw,72px) 16px clamp(32px,6vw,56px)",textAlign:"center"}}>
        <div style={{maxWidth:640,margin:"0 auto"}}>
          <div style={{display:"inline-block",background:"rgba(13,148,136,.15)",border:"1px solid rgba(13,148,136,.3)",color:"#34D399",fontSize:11,fontWeight:700,padding:"4px 14px",borderRadius:20,letterSpacing:2,textTransform:"uppercase",marginBottom:16}}>HR & Recruiter Tool</div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:"clamp(24px,4vw,44px)",fontWeight:700,color:"#fff",marginBottom:12}}>Verify Professional Certificate</h1>
          <p style={{color:"rgba(255,255,255,.65)",fontSize:14,marginBottom:32,lineHeight:1.7}}>Enter the certificate number provided by the candidate to instantly verify their NovaCert credentials.</p>
          <div style={{display:"flex",gap:10,maxWidth:520,margin:"0 auto",flexWrap:"wrap"}}>
            <input className="nova-input nova-mobile-full" value={query} onChange={e=>setQuery(e.target.value)} placeholder="e.g. NC-PRO-2026-AB1234" onKeyDown={e=>e.key==="Enter"&&search()} style={{flex:"1 1 200px",fontSize:15,background:"rgba(255,255,255,.95)"}}/>
            <button className="nova-btn nova-btn-primary nova-mobile-full" onClick={search} disabled={loading} style={{flexShrink:0,justifyContent:"center"}}>
              {loading?"Verifying...":"Verify Now"}
            </button>
          </div>
          <p style={{color:"rgba(255,255,255,.35)",fontSize:12,marginTop:10}}>Certificate number is provided directly by the candidate</p>
        </div>
      </section>

      <section style={{maxWidth:680,margin:"0 auto",padding:"40px 24px"}}>
        {result && result !== "not_found" && result !== "error" && (() => {
          const exp = expired(result.expiryDate);
          return (
            <div>
              {/* Status */}
              <div style={{background:exp?"#FEF3C7":"#F0FDF4",border:`2px solid ${exp?"#F59E0B":"#16A34A"}`,borderRadius:10,padding:"20px 24px",marginBottom:20,display:"flex",alignItems:"center",gap:14}}>
                <div style={{width:48,height:48,borderRadius:"50%",background:exp?"#F59E0B":"#16A34A",display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:22,flexShrink:0}}>{exp?"⚠":"✓"}</div>
                <div>
                  <h3 style={{fontSize:17,fontWeight:700,color:exp?"#92400E":"#15803D",marginBottom:3}}>{exp?"Certificate Expired":"Certificate Valid and Verified"}</h3>
                  <p style={{fontSize:12,color:"#6B7280"}}>Certificate #{result.certNumber} · Issued by NovaCert</p>
                </div>
              </div>

              {/* Details */}
              <div className="nova-card" style={{padding:"24px",marginBottom:16}}>
                <h3 style={{fontSize:11,fontWeight:700,color:"#0D9488",textTransform:"uppercase",letterSpacing:1.5,marginBottom:18}}>Verified Candidate Details</h3>
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(min(200px,100%),1fr))",gap:"14px 24px",fontSize:14}}>
                  {[["Full Name",result.fullName],["Certificate Number",result.certNumber],["Profession",result.profession],["Experience",result.experienceYears?`${result.experienceYears} years`:"—"],["Plan",result.plan==="advanced"?"Professional Advanced":"Professional Verified"],["Issue Date",fmt(result.issuedAt)],["Valid Until",fmt(result.expiryDate)],["City",result.city]].map(([l,v])=>(
                    <div key={l}>
                      <p style={{fontSize:11,color:"#6B7280",fontWeight:600,textTransform:"uppercase",letterSpacing:.5,marginBottom:3}}>{l}</p>
                      <p style={{fontWeight:600,color:l==="Valid Until"?(exp?"#DC2626":"#16A34A"):"#0F172A"}}>{v||"—"}{l==="Valid Until"&&exp?" — EXPIRED":""}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Verified items */}
              <div className="nova-card" style={{padding:"20px",marginBottom:16}}>
                <h3 style={{fontSize:11,fontWeight:700,color:"#6B7280",textTransform:"uppercase",letterSpacing:1,marginBottom:14}}>What Was Independently Verified</h3>
                {[
                  ["Identity verified (Aadhaar / PAN)", true],
                  ["Education credentials verified", true],
                  ["Employment history verified", true],
                  ["Skills independently assessed", result.plan==="advanced"],
                  ["Reference contacts independently called", true],
                  ["No active consumer complaints found", true],
                ].map(([label,ok])=>(
                  <div key={label} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #F9FAFB",fontSize:13,color:"#374151"}}>
                    <span style={{fontSize:14,width:20,textAlign:"center",flexShrink:0}}>{ok?"✅":"⚠️"}</span>
                    {label}
                  </div>
                ))}
              </div>

              {/* Privacy note */}
              <div style={{background:"#F0FDFA",border:"1px solid #99F6E4",borderRadius:8,padding:"14px 18px",display:"flex",gap:10,fontSize:12,color:"#0F766E",lineHeight:1.6}}>
                <span style={{fontSize:14,flexShrink:0}}>🔒</span>
                <span>This verification is confidential. Candidate profile is not publicly listed or searchable. Certificate number must be provided directly by the candidate to access these details.</span>
              </div>
            </div>
          );
        })()}

        {result === "not_found" && (
          <div style={{textAlign:"center",padding:"48px 24px"}}>
            <div style={{fontSize:48,marginBottom:12}}>🔍</div>
            <h3 style={{fontSize:20,fontWeight:700,color:"#0F172A",marginBottom:8}}>Certificate Not Found</h3>
            <p style={{color:"#6B7280",fontSize:14,lineHeight:1.7,marginBottom:20,maxWidth:380,margin:"0 auto 20px"}}>No NovaCert Professional certificate matches this number. Check the number and try again.</p>
            <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,padding:"14px 18px",fontSize:13,color:"#92400E",maxWidth:420,margin:"0 auto"}}>
              If you suspect a fraudulent certificate contact <strong>verify@novacert.in</strong> with the candidate name and number.
            </div>
          </div>
        )}

        {result === "error" && (
          <p style={{textAlign:"center",color:"#DC2626",padding:40}}>Verification failed. Please try again or contact verify@novacert.in</p>
        )}

        {!result && (
          <div style={{textAlign:"center",padding:"48px 24px",color:"#6B7280"}}>
            <div style={{fontSize:48,marginBottom:12}}>🛡️</div>
            <p style={{fontSize:14}}>Enter a certificate number above to verify a candidate's credentials</p>
          </div>
        )}

        <div style={{marginTop:32,textAlign:"center",fontSize:12,color:"#9CA3AF"}}>
          Powered by NovaCert · novacert.in · For candidates: <span style={{color:"#0D9488",cursor:"pointer"}} onClick={()=>window.location.href="/"}>apply at novacert.in</span>
        </div>
      </section>
    </>
  );
}
